import { auth, database, storage, versionReload, z } from './index';
import { endAt, equalTo, get, limitToFirst, limitToLast, off, onChildAdded, onChildChanged, onChildRemoved, onDisconnect, onValue, orderByChild, orderByKey, push, query, ref, remove, runTransaction, serverTimestamp, set, startAt, update } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-database.js';
import { httpsCallable } from 'https://www.gstatic.com/firebasejs/11.8.0/firebase-functions.js';
import { OFFLINE, getOffline, updateOffline, setOffline, removeOffline, pushOffline, transactionOffline, subscribeOffline } from './offline';
import itemsData from './data/game/items.json';
import unifontRanges from './data/unifont.json';
import unifontFullUrl from './fonts/UnifontExMono.woff2';
import unifontSmallUrl from './fonts/UnifontSmall.woff2';

// Unifont comes as two files of one family (tools/fonts/subset.mjs builds the small one and unifont.json):
// a small file holding every character the app is known to draw, loaded on every page (about 94 KB), and
// the full 2 MB file as a backup whose unicode-range is only the characters the small one lacks, so the
// browser fetches it only when text needs one of them. HTML text (every nickname) picks the right file by
// itself; canvas text does not make the browser load a font, so the canvas asks unifontCheck first.
const unicodeRange = (ranges) => ranges.map(([a, b]) => (a === b ? `U+${a.toString(16)}` : `U+${a.toString(16)}-${b.toString(16)}`)).join(', ');
const unifontSmall = new Set();
for (const [a, b] of unifontRanges.small) for (let p = a; p <= b; p++) unifontSmall.add(p);
const unifontFull = (p) => {
  let lo = 0, hi = unifontRanges.full.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1, [a, b] = unifontRanges.full[mid];
    if (p < a) hi = mid - 1;
    else if (p > b) lo = mid + 1;
    else return true;
  }
  return false;
};
// The backup is added first: for a character both cover, the face added last wins, so the small file is
// always the one used for its own characters.
export const unifontFaces = [
  new FontFace('Unifont', `url(${unifontFullUrl})`, { unicodeRange: unicodeRange(unifontRanges.full) }),
  new FontFace('Unifont', `url(${unifontSmallUrl})`, { unicodeRange: unicodeRange(unifontRanges.small) }),
];
const unifontMissed = new Set();
// Every Unifont drawing comes through here with the text it is about to draw. A character only the full
// file has is logged to the teacher log, once per character per session, which is what tells us to add it
// to the small file; the call then returns a promise that settles once the full file is loaded (only the
// first time). Ordinary text costs a set lookup per character and returns null, so drawing goes on as
// before. A character Unifont does not have at all is the device font's, exactly as it always was.
export function unifontCheck(text, doc = document) {
  let need = false;
  for (const ch of String(text ?? '')) {
    const p = ch.codePointAt(0);
    if (unifontSmall.has(p) || !unifontFull(p)) continue;
    need = true;
    if (!unifontMissed.has(p)) {
      unifontMissed.add(p);
      logMessage(`Unifont fallback: ${ch} (U+${p.toString(16).toUpperCase().padStart(4, '0')})`, { student: z.student, tooltip: navigator.userAgent });
    }
  }
  if (!need || unifontFaces[0].status === 'loaded') return null;
  return doc.fonts.load('bold 60px Unifont', text).catch(() => {});
}
// For drawing that can never be redone, the video recording: resolves only once every file the text needs
// is loaded in that document, so a recording never bakes in a stand-in glyph.
export async function unifontReady(text, doc) {
  unifontCheck(text, doc);
  await doc.fonts.load('bold 60px Unifont', text);
}
// HTML text (every nickname: nameplates, rankings, friends, galaxy, cards, the log) loads the full file by
// itself, without coming through unifontCheck. When the full file loads for any reason, the text on screen,
// in the page and every tile, is read for the characters that needed it, so those are logged as well.
unifontFaces[0].loaded.then(() => {
  const docs = [document, ...[...document.querySelectorAll('iframe')].map((frame) => { try { return frame.contentDocument; } catch (error) { return null; } })];
  for (const doc of docs) if (doc?.body) unifontCheck(doc.body.innerText, doc);
}).catch(() => {});

let connected, focused, prevError, prevParams, prevTimestamp, student, teacher;

// Eagerly fetch the 4 core KaTeX faces once at parent level, regardless of
// whether the index.html katex-style <link> has finished parsing. Creates
// FontFace objects programmatically from exact CDN URLs and adds them to
// document.fonts. Subsequent iframe mounts pull these already-loaded faces
// via their own FontFace.load() hitting the HTTP cache populated here.
// Called at app startup from index.js — covers both student and teacher
// entry paths.
let katexLoading = null;
export function loadKatex() {
  if (katexLoading) return katexLoading;
  const cdn = 'https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/fonts';
  const core = [
    { family: 'KaTeX_Main', style: 'normal', weight: 'normal', file: 'KaTeX_Main-Regular.woff2' },
    { family: 'KaTeX_Math', style: 'italic', weight: 'normal', file: 'KaTeX_Math-Italic.woff2' },
    { family: 'KaTeX_Size1', style: 'normal', weight: 'normal', file: 'KaTeX_Size1-Regular.woff2' },
    { family: 'KaTeX_Size2', style: 'normal', weight: 'normal', file: 'KaTeX_Size2-Regular.woff2' },
  ];
  katexLoading = Promise.all(core.map(c => {
    const face = new FontFace(c.family, `url(${cdn}/${c.file})`, { style: c.style, weight: c.weight });
    document.fonts.add(face);
    return face.load().catch(() => {});
  }));
  return katexLoading;
}

export function renderGlyphs(text) { return text?.replace(/\uFE0F/g, '') || ''; }
// Text a person typed (a name, a nickname) going into innerHTML: shown as the characters typed, never as markup.
// Anything that can go in as textContent should; this is for text placed inside an HTML string.
// DELEGATED DOM EVENTS (Oct 2026). A control's handler is kept ON the element (`act`) instead of as a
// listener of its own; one listener per event type per document (`delegate`) runs them, walking the
// event's path from the target outwards exactly as bubbling would, stopping where a handler stops
// propagation. So a list redrawn a hundred times adds no listeners, and a removed element takes its
// handlers with it. Elements are often created in one document and placed in another (a dashboard
// tile's), so the document is registered when the handler is set and again once the element has been
// placed. A handler that stops propagation also stops the document's other listeners for that event,
// as it did when it sat on the element; that needs this runner to be the document's FIRST listener,
// so index.js (the page) and Event.listen (each lesson or tile document) register it before their own.
const DELEGATED = ['click', 'change', 'input', 'focusout', 'pointerdown', 'mousedown', 'dragstart', 'dragover', 'drop', 'dragend'];

export function delegate(doc) {
  if (!doc || doc.delegated) return;
  doc.delegated = true;
  for (const type of DELEGATED) doc.addEventListener(type, (event) => {
    for (const element of event.composedPath()) {
      if (element.nodeType !== 1) continue;
      for (const handler of element.acts?.[event.type] || []) handler.call(element, event, element);
      if (event.cancelBubble) { event.stopImmediatePropagation(); break; }
    }
  });
}

// The top and bottom fade every scrolling list in the app has: --scroll (0 at the top, 1 at the
// bottom) drives the shared mask-image, and is set only while there is something to scroll, so a
// short list has no mask at all. Re-measured on scroll, on a resize, and whenever the content or a
// display style inside it changes (a section expanding), since a capped scroller does not resize
// when what is inside it grows. The panels are built in one document and shown in another (a
// tile's), so the observers come from the window the element is in when it is first measured.
export function fadeScroll(element) {
  let observed = null;
  const update = () => {
    const win = element.ownerDocument.defaultView;
    if (win?.ResizeObserver && observed !== win) { observed = win; new win.ResizeObserver(update).observe(element); }
    // Written only when it changes: the observer below watches this element's own style too.
    const value = element.scrollHeight > element.clientHeight + 1 ? String(Math.min(element.scrollTop / (element.scrollHeight - element.clientHeight), 1)) : '';
    if (element.style.getPropertyValue('--scroll') === value) return;
    if (value) element.style.setProperty('--scroll', value);
    else element.style.removeProperty('--scroll');
  };
  element.addEventListener('scroll', update);
  new MutationObserver(update).observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  update();
}

export function act(element, type, handler) {
  if (!element) return element;
  if (!DELEGATED.includes(type)) throw new Error(`act: ${type} is not a delegated event`);
  ((element.acts ??= {})[type] ??= []).push(handler);
  delegate(element.ownerDocument);
  queueMicrotask(() => delegate(element.ownerDocument));
  return element;
}

export function escapeHtml(text) { return String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]); }

export function getOrbit(doc = document) {
  const dialog = doc.createElement('div');
  dialog.classList.add('ui', 'white');
  dialog.style.cssText = 'padding: 6px; width: auto;';
  dialog.innerHTML = z.orbit || '';
  const overlay = doc.createElement('div');
  overlay.classList.add('overlay');
  overlay.style.cssText = 'align-items: center; display: flex; justify-content: center; pointer-events: auto;';
  overlay.appendChild(dialog);
  return { overlay, dialog };
}

// Runs `work` with the loading circle over the page until it finishes, failed or not. Shown at once:
// a wait before showing it read as a delay. For panels that wait on the database before they show;
// never for work that waits on the user (the circle would cover the dialog they have to answer).
export async function withOrbit(work, doc = document) {
  const { overlay } = getOrbit(doc);
  doc.body.appendChild(overlay);
  try {
    return await work();
  } finally {
    overlay.remove();
  }
}

// `value` may be a FUNCTION of the tag's previous value, for a tag that must only ever move one
// way. It runs inside the transaction, so the comparison is against the committed value rather
// than a local cache that may be a step behind.
export function addTag(tag, key, student, value = true) { try {
  if (z.students?.[student]?.stats?.keys?.[key]?.score) return;
  safeRunTransaction(`/students/${student}/stats/keys/${key}/tags`, (tags) => {
    let parsed = JSON.parse(tags) || {};
    let output = Array.isArray(parsed) ? Object.fromEntries(parsed.map(t => [t, true])) : parsed;
    output[tag] = typeof value === 'function' ? value(output[tag]) : value;
    write(z, ['students', student, 'stats', 'keys', key, 'tags'], JSON.stringify(output));
    return JSON.stringify(output);
  });
} catch (error) { logError(error, `adding tag ${tag} to key ${key}`); }}

// A student's trim penalty (student settings, Lesson tab; it replaced the teacher-wide "Trim
// warning" toggle, Oct 2026): 0 do nothing, 1 warn at 3x the class average (the default, and what
// the toggle did), 2 warn at 2x, 3 warn at 2x and hide correctness at 3x. A student with no setting
// of their own keeps what their teacher's old toggle said (off = 0).
// How far a student went with a problem's help, as a zone (the stat sheet's tag ring and the lesson
// tile's corner box both read it): the video's play tag or the steps' scaffold tag, { step, total }.
// The video: up to a quarter is yellow, up to half orange, past that red; a legacy tag with no
// progress is red. The steps never give the final answer away as the video does, so they top out at
// orange, and only once the LAST step is solved; half of them or more is yellow, fewer is green (user
// directives, Oct 2026). A legacy steps tag with no count is orange.
export function helpZone(tag, data) {
  const progress = typeof data === 'object' && data ? data.step / data.total : null;
  if (tag === 'scaffold') return progress === null || progress >= 1 ? 'orange' : progress >= 0.5 ? 'yellow' : 'green';
  return progress === null ? 'red' : progress <= 0.25 ? 'yellow' : progress <= 0.5 ? 'orange' : 'red';
}

export function trimPenalty(student) {
  const own = z.students?.[student]?.settings?.trim;
  if (own !== undefined && own !== null && own !== '') return Number(own) || 0;
  return z.teachers?.[z.teacher]?.settings?.trimDialog === false ? 0 : 1;
}

// Whether a problem's correctness indicators are hidden from the student: blur penalty 3 once the
// problem was blurred, buried or closed, or trim penalty 3 once its trim crossed 3x the class
// average (a 'trim' tag). The ONE reading every indicator goes through — input themes, popups, the
// slide button — so the two penalties hide exactly the same things. Tags are a JSON string in
// either format, so these are substring tests.
export function correctnessHidden(student, key) {
  const tags = z.students?.[student]?.stats?.keys?.[key]?.tags || '';
  const blur = Number(z.students?.[student]?.settings?.blur) === 3 && (tags.includes('blur') || tags.includes('bury') || tags.includes('close'));
  return blur || (trimPenalty(student) === 3 && tags.includes('"trim"'));
}

// Canonical visual identity of an inventory slot — two slots stack iff their keys
// match. sprite+style+color+anim is exactly what the player sees and what items.json
// keys on; the legacy `type` field (only ever stamped by Song of Rousing) and any
// world-junk fields are deliberately ignored so old/divergent branches still merge.
export function itemKey(item) {
  return `${item.sprite}|${item.style ?? 1}|${item.color ?? 1}|${item.anim || ''}`;
}

// THE single canonical add-to-inventory path. Mutates `inventory` (a sparse-safe
// array) in place: stacks `count` of an item onto the matching slot, else the first
// empty slot, else the end. Pass either an items.json name ('Branch', 'Seed', a berry
// name…) or a raw item object — structure is rebuilt to {name,sprite,style,color,anim}
// so EVERY write of a given item is byte-identical regardless of caller, killing the
// "branches don't stack because the structures differ" class of bug. Unique items
// never stack. Caller persists (update / out object). Returns inventory.
export function addItem(inventory, nameOrItem, count = 1) {
  const data = typeof nameOrItem === 'string' ? itemsData[nameOrItem] : nameOrItem;
  if (!data) return inventory;
  const name = typeof nameOrItem === 'string' ? nameOrItem : (nameOrItem.name ?? null);
  const item = { name, sprite: data.sprite, style: data.style ?? 1, color: data.color ?? 1, anim: data.anim ?? null };
  if (data.unique) item.unique = true;
  const idx = data.unique ? -1 : inventory.findIndex(slot => slot && itemKey(slot) === itemKey(item));
  if (idx !== -1) {
    inventory[idx].count = (inventory[idx].count || 1) + count;
  } else {
    if (!data.unique) item.count = count;
    const empty = inventory.findIndex(slot => !slot);
    if (empty !== -1) inventory[empty] = item;
    else inventory.push(item);
  }
  return inventory;
}

// A critter task's key is `task-{hearts}`, with a letter after the number when two tasks share a heart
// level (the beaver's bed and storage box are task-4a and task-4b). The number is the heart level; the
// number and letter together are the task's id, which keys the per-student flag, the queue's `task`
// and the goal node. An id with no letter stays a NUMBER on the queue, as every order before the
// letters was written.
export const TASK = /^task-([0-9]+)([a-z]?)$/;
export const taskId = (key) => { const m = TASK.exec(key); return !m ? null : m[2] ? m[1] + m[2] : Number(m[1]); };

// Self-healing guardrail: collapse any duplicate non-unique stacks (same itemKey) into
// the first slot, summing counts and nulling the rest. Defends against inventories that
// were split by historically-divergent add paths or legacy data. Mutates in place;
// returns true iff anything was merged (so the owner can persist the fix exactly once).
export function mergeInventory(inventory) {
  const seen = {};
  let changed = false;
  for (let i = 0; i < inventory.length; i++) {
    const slot = inventory[i];
    if (!slot || slot.unique) continue;
    const key = itemKey(slot);
    if (seen[key] === undefined) seen[key] = i;
    else {
      inventory[seen[key]].count = (inventory[seen[key]].count || 1) + (slot.count || 1);
      inventory[i] = null;
      changed = true;
    }
  }
  return changed;
}

// Formats a time in seconds as H:MM:SS once it reaches an hour, M:SS below that. Shared by
// the dash stat sheets, the lesson tile timer (app.js) and the game's fact timer (game.js)
// so all three read identically - they were three separate copies, and the two timers had
// no hours field at all, so a 78-minute problem showed "78:23".
export function formatTime(v) {
  let s = Math.round(v);
  let h = Math.floor(s / 3600);
  let m = Math.floor((s % 3600) / 60);
  let sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function capitalize(string, all) {
  if (all) return string ? string.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : '';
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
}

// Title-case a name: first letter of each whitespace-separated word uppercase,
// the rest lowercase. Used on student-initiated name input ("SUZIE" -> "Suzie",
// "ha jin" -> "Ha Jin"). capitalize() preserves remaining letters as-is and
// would leave "SUZIE" alone.
export function titleCase(string) {
  if (!string) return '';
  return string.split(/(\s+)/).map(part => /\s+/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join('');
}

// Music and sound-effects volume: a 0-100 slider stored as a number at
// /students/{id}/settings/{music|sound}. Records from the old On/Off dropdown hold
// 'on', 'off' or nothing — 'off' reads as 0, 'on' and unset as the 25% default.
// 50% is what 'on' used to sound like (see MUSIC_GAIN / SOUND_GAIN in game.js).
export function getVolume(value) {
  if (typeof value === 'number') return Math.max(0, Math.min(value, 100));
  return value === 'off' ? 0 : 25;
}

function decapitalize(string, all) {
  if (all) return string ? string.split(' ').map(word => word.charAt(0).toLowerCase() + word.slice(1)).join(' ') : '';
  else return string ? string.charAt(0).toLowerCase() + string.slice(1) : '';
}

export function delay(ms, start, src, obj, type, generation, checkGeneration) {
  let delay = 0;
  let pause = 0;
  let timeout;
  const initialDelay = ms - (Date.now() - start);
  //console.log(`🕐 delay() called: point at ${ms}ms, playStartTime=${start}, initial delay=${initialDelay}ms, generation=${generation}`);
  
  return new Promise(resolve => {
    let newTimeout = (time) => {
      //console.log(`⏱️  Setting timeout for ${time}ms (point at ${ms}ms, generation ${generation})`);
      timeout = setTimeout(() => {
        // Check if this generation is still valid before executing
        if (checkGeneration && checkGeneration() !== generation) {
          //console.log(`❌ Cancelling drawing at ${ms}ms - generation mismatch (expected ${generation}, got ${checkGeneration()})`);
          clearInterval(interval);
          resolve('cancelled');
          return;
        }
        if (src[obj] === 'playing' || src[obj] === 'ended') {
          //console.log(`✅ Executing drawing at ${ms}ms after ${time}ms delay (generation ${generation})`);
          clearInterval(interval);
          resolve('played');
        }
      }, time);
    }
    newTimeout(initialDelay);
    let interval = setInterval(() => {
      // Check generation validity periodically
      if (checkGeneration && checkGeneration() !== generation) {
        //console.log(`❌ Cancelling drawing at ${ms}ms during interval - generation mismatch`);
        clearInterval(interval);
        clearTimeout(timeout);
        resolve('cancelled');
        return;
      }
      if (!src[obj]) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      } else if (src[obj] === 'paused' && pause === 0) {
        pause = Date.now() - delay - start;
        clearTimeout(timeout);
      } else if (src[obj] === 'paused') {
        delay += 100;
      } else if (src[obj] === 'playing' && pause > 0) {
        newTimeout(ms - pause - (Date.now() - start));
        pause = 0;
      }
    }, 100);   
  });
}

// Clamp a panel's position to ensure it stays within viewport
// Returns clamped { left/right, top } as percentage strings, or null if no clamping needed
export function clampPanelPosition(element, win, pos, isRightAligned = false) {
  if (!element || !pos) return pos;
  
  const rect = element.getBoundingClientRect();
  const winWidth = win.innerWidth;
  const winHeight = win.innerHeight;
  const toolbarHeight = 36;
  
  // Convert percentage to pixels for calculation
  let x, y;
  if (isRightAligned && pos.right) {
    x = (parseFloat(pos.right) / 100) * winWidth;
  } else if (pos.left) {
    x = (parseFloat(pos.left) / 100) * winWidth;
  }
  if (pos.top) {
    y = (parseFloat(pos.top) / 100) * winHeight;
  }
  
  let clamped = false;
  
  // Clamp horizontal
  if (isRightAligned) {
    if (x !== undefined) {
      const minX = 0;
      const maxX = winWidth - rect.width;
      if (x < minX) { x = minX; clamped = true; }
      if (x > maxX) { x = maxX; clamped = true; }
    }
  } else {
    if (x !== undefined) {
      const minX = 0;
      const maxX = winWidth - rect.width;
      if (x < minX) { x = minX; clamped = true; }
      if (x > maxX) { x = maxX; clamped = true; }
    }
  }
  
  // Clamp vertical
  if (y !== undefined) {
    const minY = toolbarHeight;
    const maxY = winHeight - rect.height;
    if (y < minY) { y = minY; clamped = true; }
    if (y > maxY) { y = maxY; clamped = true; }
  }
  
  if (!clamped) return pos;
  
  // Return clamped values as percentage strings
  const result = {};
  if (isRightAligned) {
    result.right = `${(x / winWidth) * 100}%`;
  } else {
    result.left = `${(x / winWidth) * 100}%`;
  }
  result.top = `${(y / winHeight) * 100}%`;
  return result;
}
export function drag(element, doc, win, offsetX, offsetY, event, context) { try {
  let html = doc.documentElement;
  // The lowest a panel may reach: the top of the toolbox less the toolbox's own bottom margin, so
  // the gap above it equals the gap below it. Measured, so it follows the toolbox's height (it went
  // from 43.8px to 40 when its buttons became 32px) and holds whatever the panel's container is
  // offset by: a fixed toolbox height plus 5.5 assumed a container at the top of the page, and a
  // panel inside a slide (36px down) was dragged 32px into the toolbox.
  let toolbox = html.querySelector('.toolbox')?.getBoundingClientRect();
  let lowest = toolbox?.height ? toolbox.top - (win.innerHeight - toolbox.bottom) : win.innerHeight;
  let rect = element.getBoundingClientRect();
  let scale = html.style.getPropertyValue('--session-scale-x') ? { x: parseFloat(html.style.getPropertyValue('--session-scale-x')), y: parseFloat(html.style.getPropertyValue('--session-scale-y')) } : { x: 1, y: 1 };
  let vmin = Math.min(win.innerWidth / 500, win.innerHeight / 500);
  let [winHeight, winWidth] = [win.innerHeight - 36 * scale.y, win.innerWidth];
  let clientX, clientY, x, y;
  // Use the offsetParent as the vertical coordinate space so panels inside
  // non-viewport containers (e.g. slide-div) stay put and so cursor-relative
  // grip is preserved during drag. parentTop is the offsetParent's viewport
  // offset; topDenom is the denominator that matches CSS top percentage.
  let parentRect = element.offsetParent ? element.offsetParent.getBoundingClientRect() : null;
  let parentTop = parentRect ? parentRect.top : 0;
  let topDenom = parentRect ? parentRect.height : win.innerHeight;

  let toolbar = 36 * scale.y;
  if (element.classList.contains('right')) {
    if (event) {
      [clientX, clientY] = event.touches ? [event.touches[0].clientX, event.touches[0].clientY] : [event.clientX, event.clientY];
      [x, y] = [winWidth - (clientX + offsetX), clientY - parentTop - offsetY];
    } else [x, y] = [winWidth - rect.right, rect.top - parentTop - toolbar];
    // Clamp x to valid range
    x = Math.max(vmin, Math.min(x, winWidth - rect.width));
    // Clamp y to valid range
    y = Math.max(-toolbar, Math.min(y, lowest - rect.height - parentTop - toolbar));
    element.style.right = `${x / winWidth * 100}%`;
    element.style.top = `${(y + toolbar) / topDenom * 100}%`;
    if (rect.width && rect.height) safeUpdate(`/teachers/${z.teacher}/online/${html.getAttribute('data-session')}/${element.id.replace('-div', '')}`, { right: `${x / winWidth * 100}%`, top: `${(y + toolbar) / topDenom * 100}%` });
  } else {
    if (event) {
      [clientX, clientY] = event.touches ? [event.touches[0].clientX, event.touches[0].clientY] : [event.clientX, event.clientY];
      [x, y] = [clientX - offsetX, clientY - parentTop - offsetY];
    } else [x, y] = [rect.left, rect.top - parentTop - toolbar];
    // Clamp x to valid range
    x = Math.max(0, Math.min(x, winWidth - rect.width));
    // Clamp y to valid range
    y = Math.max(-toolbar, Math.min(y, lowest - rect.height - parentTop - toolbar));
    element.style.left = `${x / winWidth * 100}%`;
    element.style.top = `${(y + toolbar) / topDenom * 100}%`;
    if (rect.width && rect.height) safeUpdate(`/teachers/${z.teacher}/online/${html.getAttribute('data-session')}/${element.id.replace('-div', '')}`, { left: `${x / winWidth * 100}%`, top: `${(y + toolbar) / topDenom * 100}%` });
  }
} catch (error) {
  let message = `${z.name ? z.name : 'Someone'} encountered an error dragging an element '${element.id}'${context ? ` when ${context}` : ''}: ${error}`;
  console.error(message);
  logMessage(message, { app: z.app, code: z.code, session: doc.documentElement.getAttribute('data-session'), student: doc.documentElement.getAttribute('data-student'), teacher: z.teacher, tooltip: navigator.userAgent, type: 'error', version: process.env.VERSION });
}}

export function equal(a, b) {
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (!equal(a[i], b[i])) return false;
    } else {
      if (Object.keys(a).length !== Object.keys(b).length) return false;
      for (let key in a) if (!equal(a[key], b[key])) return false;
    }
  } else if (a !== b) return false;
  return true;
}

function erase(obj, path) {
  let objData = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (!objData[path[i]]) return;
    objData = objData[path[i]];
  }
  delete objData[path[path.length - 1]];
}

export function escape(string) {
  return string.replace(/([!\"#$%&'()*+,.\/:;<=>?@[\\]^`{|}~])/g, '\\$1');
}

export function evaluate(expression) {
  let tokens = [];
  let operatorStack = [];
  let operators = ['+', '-', '×', '*', '÷', '/', '^', '√'];
  let precedence = { '+': 1, '-': 1, '×': 2, '*': 2, '÷': 2, '/': 2, '^': 3, '√': 4 };
  expression = expression.replace(/(\d+)(π|√)/g, "$1×$2");
  expression.match(/(\d+\.\d+|\d+|π|\+|\-|×|\*|÷|\/|\^|√)/g).forEach((token) => {
    if (isNumber(token) || token === 'π') {
      tokens.push(token);
    } else if (operators.includes(token)) {
      while (operators.includes(operatorStack[operatorStack.length - 1]) && precedence[token] <= precedence[operatorStack[operatorStack.length - 1]]) tokens.push(operatorStack.pop());
      operatorStack.push(token);
    }
  });
  while (operatorStack.length > 0) tokens.push(operatorStack.pop());
  let stack = [];
  tokens.forEach((token) => {
      if (isNumber(token)) {
        stack.push(parseFloat(token));
      } else if (token === 'π') {
        stack.push(Math.PI);
      } else if (token === '√') {
        stack.push(Math.sqrt(stack.pop()));
      } else if (token === '+') {
        stack.push(stack.pop() + stack.pop());
      } else if (token === '-') {
        let subtractor = stack.pop();
        stack.push(stack.pop() - subtractor);
      } else if (token === '×' || token === '*') {
        stack.push(stack.pop() * stack.pop());
      } else if (token === '÷' || token === '/') {
        let divisor = stack.pop();
        stack.push(stack.pop() / divisor);
      } else if (token === '^') {
        let exponent = stack.pop();
        let base = stack.pop();
        stack.push(Math.pow(base, exponent));
      }
  });
  return stack[0];
}

export async function getCrashDialog(header, message) {
  let dialog = document.createElement('div');
  dialog.classList.add('dialog', 'ui');
  dialog.appendChild(getHeader(header, dialog));
  let dialogMessage = document.createElement('div');
  dialogMessage.classList.add('dialog-text');
  dialogMessage.innerHTML = message;
  dialog.appendChild(dialogMessage);
  let refreshButton = document.createElement('button');
  refreshButton.id = 'refresh-button';
  refreshButton.innerHTML = 'Refresh';
  act(refreshButton, 'click', () => window.location.reload());
  dialog.appendChild(refreshButton);
  if (document.querySelector('.app-div')) document.querySelector('.app-div').remove();
  if (document.querySelector('.dialog')) document.querySelector('.dialog').remove();
  if (document.querySelector('.toolbar')) document.querySelector('.toolbar').remove();
  document.body.appendChild(dialog);
}

export function getDate() {
  let date = new Date();
  let months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  let day = date.getDate();
  let year = date.getFullYear();
  let suffix;
  if (day === 1 || day === 21 || day === 31) suffix = "st";
  else if (day === 2 || day === 22) suffix = "nd";
  else if (day === 3 || day === 23) suffix = "rd";
  else suffix = "th";
  return { numerical: `${date.getMonth() + 1}/${day}/${year}`, textual: `${months[date.getMonth()]} ${day}${suffix}, ${year}` };
}

export async function getDialog(required, header, message, option1, func1, option2, func2, opt8, opt9, opt10, opt11) {
  // The `z` object is created once in index.js and shared across the whole
  // session, so `z.doc` always points to the top frame's document. Callers
  // running inside an iframe (Game / Action / Fact instances created by the
  // dashboard) must pass `this.doc` so the dialog mounts in their iframe and
  // its overlay covers the iframe's full viewport (including the in-game
  // toolbar) rather than the parent dashboard.
  //
  // Argument-shape compatibility: prefer `containerDoc` as the 8th arg (the
  // common new shape — `getDialog(req, hdr, msg, opt1, fn1, opt2, fn2, this.doc)`).
  // Legacy callers pass `(timer, timerFunc, cancelFunc, containerDoc)` in slots
  // 8-11; we detect a Document by `nodeType === 9`.
  let timer, timerFunc, cancelFunc, containerDoc;
  if (opt8 && typeof opt8 === 'object' && opt8.nodeType === 9) {
    containerDoc = opt8;
  } else {
    timer = opt8; timerFunc = opt9; cancelFunc = opt10; containerDoc = opt11;
  }
  if (z.hideDialogs && !func1 && !option2 && !required) return 1;
  return new Promise(async (resolve) => {
    let timeout;
    const _doc = containerDoc || z.doc;
    // Append overlay+dialog skeleton SYNCHRONOUSLY before any await — otherwise
    // a caller that gates re-entry on `body.querySelector('.dialog')` races: the
    // microtask boundary on `getHeader()` lets per-frame triggers (e.g. the
    // tallgrass `plantSeed` check, teleportation prompts) stack hundreds of overlays
    // before the first one actually mounts, which compounds the translucent black
    // backdrop into an opaque one.
    let dialog = _doc.createElement('div');
    dialog.classList.add('dialog', 'ui');
    let dialogOverlay = _doc.createElement('div');
    dialogOverlay.classList.add('overlay', 'dialog');
    if (header === 'Paused') dialogOverlay.classList.add('paused');
    dialogOverlay.appendChild(dialog);
    _doc.body.appendChild(dialogOverlay);
    dialog.appendChild(getHeader(header, dialog, false, null, _doc));
    let dialogMessage = _doc.createElement('div');
    dialogMessage.classList.add('dialog-text');
    dialogMessage.innerHTML = message;
    if (header === 'Feedback') window.renderMathInElement(dialogMessage);
    if (header === 'Privacy Policy') {
      dialog.style.height = '320px';
      dialog.style.paddingBottom = '6px';
      dialog.style.width = '320px';
      dialogMessage.classList.add('scroll');
      dialogMessage.style.height = '224px';
    }
    dialog.appendChild(dialogMessage);
    if (option1) {
      let yesButton = _doc.createElement('button');
      yesButton.classList.add('dialog-yes-button');
      yesButton.innerHTML = option1;
      dialog.appendChild(yesButton);
      act(yesButton, 'click', () => {
        dialogOverlay.remove();
        clearTimeout(timeout);
        if (func1) func1();
        resolve(1);
      });
    }
    if (option2) {
      dialog.classList.add('option');
      let noButton = _doc.createElement('button');
      noButton.classList.add('dialog-no-button');
      noButton.innerHTML = option2;
      dialog.appendChild(noButton);
      act(noButton, 'click', () => {
        dialogOverlay.remove();
        clearTimeout(timeout);
        if (func2) func2();
        resolve(2);
      });
    }
    let openedAt = Date.now();
    if (!required) act(dialogOverlay, 'click', (event) => {
      if (Date.now() - openedAt < 300) return;
      if (event.target.closest('.dialog.ui')) return;
      dialogOverlay.remove();
      clearTimeout(timeout);
      if (cancelFunc) cancelFunc();
      resolve('cancelled');
    });
    if (timer) timeout = setTimeout(() => {
      dialogOverlay.remove();
      if (timerFunc) timerFunc();
      resolve('timeout');
    }, timer);
    if (!option1) resolve();
  });
}

// ---- Dialogs a student sees ----
// A dialog on a student's screen is written on their session record, /teachers/{t}/online/{session}/
// dialogs/{id} = { header, message, option1, option2, required }, and drawn from the echo by
// drawDialogs on every session following it: the student's own device and a teacher's tile alike. A
// click on either screen writes the `answer` (1, 2 or 'cancelled', what getDialog resolves with); the
// device that asked resolves with it and removes the record, which closes it on every screen. A timer
// is kept by the asking device, which answers 'timeout'. Callbacks never travel: they run where the
// dialog was asked, from the promise this returns.
const asked = {};
// An answer still in flight when the asking device removes the record is cancelled by Firebase ("set"),
// and that is fine: the dialog it answered is already closed everywhere.
const answerDialog = (path, value) => runTransaction(ref(database, path), (dialog) => dialog && dialog.answer == null ? { ...dialog, answer: value } : undefined).catch(() => {});
export function askDialog(session, { required = false, header, message, option1 = 'Got it!', option2 = null, timer = null }) {
  if (!z.teacher || !session) return getDialog(required, header, message, option1, null, option2, null, z.doc);
  const record = push(ref(database, `/teachers/${z.teacher}/online/${session}/dialogs`));
  return new Promise((resolve) => {
    asked[record.key] = resolve;
    set(record, { header, message, option1, option2, required });
    if (timer) setTimeout(() => { if (asked[record.key]) answerDialog(`/teachers/${z.teacher}/online/${session}/dialogs/${record.key}`, 'timeout'); }, timer);
  });
}
export function drawDialogs(doc, list, path) { try {
  for (const shown of doc.querySelectorAll('.overlay[data-dialog]')) {
    const item = list?.[shown.dataset.dialog];
    if (!item || item.answer != null) shown.remove();
  }
  for (const [id, item] of Object.entries(list || {})) {
    if (item?.answer != null) {
      const resolve = asked[id];
      if (resolve) { delete asked[id]; remove(ref(database, `${path}/${id}`)); resolve(item.answer); }
      continue;
    }
    if (!item?.message || doc.querySelector(`.overlay[data-dialog="${id}"]`)) continue;
    getDialog(!!item.required, item.header, item.message, item.option1, null, item.option2, null, doc).then((answer) => answerDialog(`${path}/${id}`, answer ?? 'cancelled'));
    // getDialog mounts its overlay before its first await, so it is the body's last child here.
    doc.body.lastElementChild.dataset.dialog = id;
  }
} catch (error) { logError(error, 'drawing the dialogs'); }}

// A fairy's conversation box glows in its colour (the conversation dialogs' own fairy colours); Doremi's
// in the colour of whichever of its seven is speaking. On the student's screen and a watching tile alike.
const VOICE_COLOR = { Do: '#ea4f36', Re: '#f79617', Mi: '#fbff86', Fa: '#91db69', So: '#30e1b9', La: '#a884f3', Ti: '#f04f78' };
export function voiceDialog(dialog, voice) {
  if (!VOICE_COLOR[voice]) return;
  dialog.classList.add('voiced');
  dialog.style.setProperty('--voice', VOICE_COLOR[voice]);
}

// Doremi's health bar fill: a smooth gradient through its AURA's colours (user directive, Oct 2026), the
// stops being its prism overlay's own colours round the wheel, read off the sheet by tools/art/aura-ramp.mjs.
const AURA = ['#d68a89', '#f0a960', '#d5d569', '#addf7c', '#90dc91', '#88cba9', '#8abcbb', '#97a8b8', '#9ea0c2', '#a797b6', '#b48db5', '#c28aa7'];
export function auraBar() {
  return `linear-gradient(90deg, ${AURA.join(', ')})`;
}

// Synchronous on purpose — `async` was decorative, the body never awaits anything.
// Keeping it sync removes a microtask boundary at every callsite that did
// `dialog.appendChild(getHeader(...))`. That boundary used to let per-frame
// or listener-driven dialog creators (plantSeed, listenSession convo, etc.) race
// past `body.querySelector('.dialog')` gates and stack hundreds of overlays.
export function getHeader(title, container, focusable, close, containerDoc) {
  const _doc = containerDoc || z.doc;
  let header = _doc.createElement('div');
  header.classList.add('header', title ? null : 'untitled');

  let headerLeft = _doc.createElement('div');
  headerLeft.classList.add('header-left');
  header.appendChild(headerLeft);

  let headerCenter = _doc.createElement('div');
  headerCenter.classList.add('header-center');
  let headerTitle = _doc.createElement('div');
  headerTitle.classList.add('header-title');
  if (title && title.includes('https://')) {
    header.style.height = '100px';
    let headerImage = _doc.createElement('img');
    headerImage.src = title;
    headerImage.style.border = '3px solid rgb(0, 0, 0, 0.2)';
    headerImage.style.borderRadius = '50%';
    headerImage.style.height = '75px';
    headerImage.style.marginTop = '3px';
    headerImage.style.width = '75px';
    headerTitle.appendChild(headerImage);
  } else headerTitle.innerHTML = title;
  headerCenter.appendChild(headerTitle);
  header.appendChild(headerCenter);

  let headerRight = _doc.createElement('div');
  headerRight.classList.add('header-right');
  if (focusable) {
    let expandButton = _doc.createElement('button');
    expandButton.innerHTML = z.svg.icons.expand;
    let dash = _doc.getElementById('dash');
    act(expandButton, 'click', () => {
      if (container.classList.contains('focused')) {
        expandButton.innerHTML = z.svg.icons.expand;
        container.classList.remove('focused');
        if (dash) dash.classList.remove('unfocused');
      } else {
        expandButton.innerHTML = z.svg.icons.contract;
        container.classList.add('focused');
        if (dash) dash.classList.add('unfocused');
      }
    });
    headerRight.appendChild(expandButton);
  }
  if (close) {
    let closeButton = _doc.createElement('button');
    closeButton.innerHTML = z.svg.icons.close;
    act(closeButton, 'click', () => close());
    headerRight.appendChild(closeButton);
  }
  header.appendChild(headerRight);
  return header;
}

export async function getInputDialog(containerDoc, header, message, placeholder, func) {
  return new Promise(async (resolve) => {
    const _doc = containerDoc || z.doc;
    let dialog = _doc.createElement('div');
    dialog.classList.add('dialog', 'ui');
    dialog.appendChild(getHeader(header, dialog, false, null, _doc));
    let dialogMessage = _doc.createElement('div');
    dialogMessage.classList.add('dialog-text');
    dialogMessage.innerHTML = message;
    dialog.appendChild(dialogMessage);
    let input = _doc.createElement('input');
    input.classList.add('dialog-input');
    if (placeholder) input.placeholder = placeholder;
    dialog.appendChild(input);
    let submitButton = _doc.createElement('button');
    submitButton.innerHTML = 'Submit';
    submitButton.style.marginTop = '6px';
    dialog.appendChild(submitButton);
    let dialogOverlay = _doc.createElement('div');
    dialogOverlay.classList.add('overlay', 'dialog');
    dialogOverlay.appendChild(dialog);
    _doc.body.appendChild(dialogOverlay);
    let openedAt = Date.now();
    act(dialogOverlay, 'click', (event) => {
      if (Date.now() - openedAt < 300) return;
      if (event.target.closest('.dialog.ui')) return;
      dialogOverlay.remove();
      resolve('cancelled');
    });
    act(submitButton, 'click', () => {
      if (input.value === '') return;
      dialogOverlay.remove();
      if (func) func(input.value);
      resolve(input.value);
    });
  });
}

// Two boxes, one per pass: the script (what is SAID) and the annotations (what is DRAWN). They
// used to share one box, which the narration and annotator each had to be told to read for the
// part that pertained to them — and each still acted on the other's half. Placeholders carry the
// labels; there is no text outside the boxes. Resolves { script, annotation } (each null when
// empty) on Generate, null on dismiss. Deleting a video lives in the toolbag inspector.
export async function getVideoPromptDialog(containerDoc) {
  return new Promise((resolve) => {
    let dialog = document.createElement('div');
    dialog.classList.add('dialog', 'ui');
    dialog.insertBefore(getHeader('What else?', dialog), dialog.firstChild);
    let dialogMessage = document.createElement('div');
    dialogMessage.classList.add('dialog-text');
    dialogMessage.innerHTML = 'Add optional instructions<br>for the video generation.';
    dialog.appendChild(dialogMessage);
    const textarea = {};
    // Alphabetical: annotation above narration. The narration box is still `script` in code —
    // that is the field name the narration CF and Video.generate already use.
    for (const [name, placeholder] of [['annotation', 'Annotation instructions'], ['script', 'Narration instructions']]) {
      textarea[name] = document.createElement('textarea');
      textarea[name].classList.add('dialog-input', 'video-prompt-input');
      textarea[name].placeholder = placeholder;
      dialog.appendChild(textarea[name]);
    }
    let generateButton = document.createElement('button');
    generateButton.innerHTML = 'Generate';
    generateButton.style.marginTop = '6px';
    dialog.appendChild(generateButton);
    let resolved = false;
    let dialogOverlay = document.createElement('div');
    dialogOverlay.classList.add('overlay', 'dialog');
    dialogOverlay.appendChild(dialog);
    containerDoc.body.appendChild(dialogOverlay);
    let openedAt = Date.now();
    act(dialogOverlay, 'click', (event) => {
      if (Date.now() - openedAt < 300) return;
      if (event.target.closest('.dialog.ui')) return;
      if (resolved) return;
      resolved = true;
      dialogOverlay.remove();
      resolve(null);
    });
    act(generateButton, 'click', () => {
      if (resolved) return;
      resolved = true;
      dialogOverlay.remove();
      resolve({ script: textarea.script.value.trim() || null, annotation: textarea.annotation.value.trim() || null, audioContext: getAudioContext() });
    });
  });
}

// The AudioContext a video recording plays its narration through. Called INSIDE the click that
// starts a generation (Generate, or Yes on the resume question), because that is the last user
// gesture before a pipeline that runs for minutes. Safari only unlocks audio inside a gesture: a
// context made when the recording finally starts stays suspended and the capture is silent or
// never ends, and an HTMLMediaElement.play() at that point is refused outright (NotAllowedError
// — the "have to refresh to record again" report). Video.generate closes it when done.
function getAudioContext() {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 44100, latencyHint: 'playback' });
    audioContext.resume().catch(() => {});
    return audioContext;
  } catch (error) { logError(error, 'creating the audio context for the video recording'); return null; }
}

// Before a video generation: an attempt for this key that stopped partway leaves a checkpoint at
// /teachers/{t}/recording/{key}, written by Video.generate the moment the narration — the part
// that costs money — is safely in storage, and removed when a run completes. Ask whether to pick
// it up. Resolves { resume: checkpoint, audioContext } to continue (the context made inside the
// Yes click, see getAudioContext), { resume: null } to start over (checkpoint discarded), or null
// when the question was dismissed. A run that failed before the narration existed leaves no
// checkpoint: nothing worth keeping was made.
export async function getVideoResumeDialog(containerDoc, key) {
  const path = `/teachers/${z.teacher}/recording/${key}`;
  const checkpoint = await safeGet(path, { cache: false });
  if (!checkpoint?.audioUrl) return { resume: null };
  let audioContext = null;
  const choice = await getDialog(false, 'Resume?', 'A previous recording attempt<br>failed partway through.<br>Resume where it left off?', 'Yes', () => { audioContext = getAudioContext(); }, 'No', null, containerDoc);
  if (choice === 1) return { resume: checkpoint, audioContext };
  if (choice === 2) {
    safeRemove(path, `discarding the recording checkpoint for ${key}`);
    return { resume: null };
  }
  return null;
}

export async function getMessageDialog(header, message, progress, containerDoc, warning) {
  const _doc = containerDoc || z.doc;
  let dialog = _doc.createElement('div');
  dialog.classList.add('dialog', 'ui');
  dialog.appendChild(getHeader(header, dialog, false, null, _doc));
  let dialogMessage = _doc.createElement('div');
  dialogMessage.classList.add('dialog-text');
  dialogMessage.innerHTML = message;
  dialog.appendChild(dialogMessage);
  if (progress) {
    let dialogProgressDiv = _doc.createElement('div');
    dialogProgressDiv.classList.add('dialog-progress-div');
    let dialogProgressBar = _doc.createElement('div');
    dialogProgressBar.classList.add('dialog-progress-bar');
    dialogProgressDiv.appendChild(dialogProgressBar);
    dialog.appendChild(dialogProgressDiv);
  }
  if (warning) {
    let warningDiv = _doc.createElement('div');
    warningDiv.style.cssText = 'background:rgb(50,45,45);color:rgb(250,225,225);border-radius:4px;padding:6px 10px;margin-top:6px;text-align:center;font-size:13px;flex-basis:100%;';
    warningDiv.innerHTML = warning;
    dialog.appendChild(warningDiv);
  }
  let dialogOverlay = _doc.createElement('div');
  dialogOverlay.classList.add('overlay', 'dialog');
  dialogOverlay.appendChild(dialog);
  _doc.body.appendChild(dialogOverlay);
}

export async function getMultiDialog(required, header, sections) {
  return new Promise(async (resolve) => {
    let dialog = document.createElement('div');
    dialog.classList.add('dialog', 'ui');
    dialog.appendChild(getHeader(header, dialog));
    let dialogOverlay = document.createElement('div');
    dialogOverlay.classList.add('overlay', 'dialog'); 
    for (let section of sections) {
      let sectionText = document.createElement('div');
      sectionText.classList.add('dialog-text');
      sectionText.innerHTML = section.text;
      // Scoped to getMultiDialog only. Target: make the gap above this text
      // equal the gap below the previous section's text (i.e., the visible
      // space between a .dialog-text and the button right beneath it, which
      // measured ~10px in practice). The dialog is display:flex;flex-wrap:wrap
      // with align-content:normal, which absorbs ~4–5px of cross-axis
      // margin-top on wrapped items — so 14px inline → ~10px visible.
      if (sections.indexOf(section) > 0) sectionText.style.marginTop = '14px';
      dialog.appendChild(sectionText);
      let sectionButton = document.createElement('button');
      sectionButton.innerHTML = section.button;
      act(sectionButton, 'click', () => {
        dialogOverlay.remove();
        resolve(decapitalize(section.button));
      });
      dialog.appendChild(sectionButton);
    }
    dialogOverlay.appendChild(dialog);
    document.body.appendChild(dialogOverlay);
    let openedAt = Date.now();
    if (!required) act(dialogOverlay, 'click', (event) => {
      if (Date.now() - openedAt < 300) return;
      if (event.target.closest('.dialog.ui')) return;
      dialogOverlay.remove();
      resolve('cancelled');
    });
  });
}

export function getList(string) {
  if (!string) return '';
  let lines = string.split('<br>');
  // Merge non-bullet lines that follow a bullet line INTO that bullet's content
  // (as <br>-separated continuations), so the whole semantic bullet \u2014 and any
  // multi-line tail \u2014 lives inside one <li> and dims together when checked.
  let items = [];
  lines.forEach((line, index) => {
    let isBullet = /^(\d+\.|\u2022|[a-z]\.|\u25e6)/i.test(line);
    if (isBullet || items.length === 0 || !items[items.length - 1].isBullet) {
      items.push({ line, index, isBullet });
    } else {
      items[items.length - 1].line += '<br>' + line;
    }
  });
  let output = '';
  let level = 0;
  let ol;
  items.forEach((item, mIdx) => {
    let { line, index } = item;
    let topMatch = line.match(/^(\d+)\./);
    let nestedMatch = line.match(/^([a-z])\./i);
    if (topMatch || line.match(/^\u2022/)) {
      ol = !!topMatch;
      let mark = topMatch ? topMatch[1] : null;
      if (level === 2) {
        output += ol ? '</ol></li>' : '</ul></li>';
        level--;
      }
      if (level === 0) {
        output += ol ? '<ol>' : '<ul>';
        level = 1;
      }
      let content = mark ? line.slice(mark.length + 2) : line.slice(2);
      let markSpan = mark ? `<span class="ins-mark">${mark}.</span> ` : '';
      output += `<li data-i="${index}"><div class="ins-row"><span class="ins-check"></span>${markSpan}${content}</div>`;
      let next = items[mIdx + 1];
      if (next && (next.line.match(/^[a-z]\./i) || next.line.match(/^\u25e6/))) {
        output += ol ? '<ol>' : '<ul>';
        level = 2;
      } else {
        output += '</li>';
      }
    } else if (nestedMatch || line.match(/^\u25e6/)) {
      let mark = nestedMatch ? nestedMatch[1] : null;
      if (level < 2) {
        if (level === 0) {
          output += ol ? '<ol><li><ol>' : '<ul><li><ul>';
          level = 2;
        } else {
          output += ol ? '<ol>' : '<ul>';
          level = 2;
        }
      }
      let content = mark ? line.slice(mark.length + 2) : line.slice(2);
      let markSpan = mark ? `<span class="ins-mark">${mark}.</span> ` : '';
      output += `<li data-i="${index}"><div class="ins-row"><span class="ins-check"></span>${markSpan}${content}</div></li>`;
    } else output += `<span>${line}</span>`;
  });
  while (level > 0) {
    output += ol ? '</ol>' : '</ul>';
    if (level > 1) {
      output += '</li>';
    }
    level--;
  }
  return output;
}

export async function getNametag(student, label, tooltip) { try {
  let nametag = document.createElement('div');
  nametag.classList.add('nametag');
  if (label) nametag.classList.add('small');
  let name, color, emoji;
  if (student === 'dash') [name, color, emoji] = ['Mr. Rieth', null, null];
  else if (student == null) [name, color, emoji] = ['Teacher', { value: '#73e699', dark: '#0a6629' }, { name: 'apple' }];
  else for (let type of ['roster', 'new']) {
    name = z.teachers?.[z.teacher]?.[type]?.[student]?.name || await safeGet(`/teachers/${z.teacher}/${type}/${student}/name`) || 'Mr. Rieth';
    color = z.teachers?.[z.teacher]?.[type]?.[student]?.color || await safeGet(`/teachers/${z.teacher}/${type}/${student}/color`) || null;
    emoji = z.teachers?.[z.teacher]?.[type]?.[student]?.emoji || await safeGet(`/teachers/${z.teacher}/${type}/${student}/emoji`) || null;
    if (color || emoji) break;
  }
  nametag.innerHTML = `${label ? '' : escapeHtml(name)}${emoji ? z.svg.icons[emoji.name] : ''}${label ? ' ' : ''}${label ? label : ''}`;
  if (color) nametag.style.background = `linear-gradient(to bottom, ${color.value} -150%, ${color.dark} 150%)`;
  if (tooltip) nametag.title = tooltip;
  return nametag;
} catch (error) { logError(error, `getting nametag for student ${student}`); }}

export function getOrdinal(number) {
  let ordinals = ['next', "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth", "eleventh", "twelfth", "thirteenth", "fourteenth", "fifteenth", "sixteenth", "seventeenth", "eighteenth", "nineteenth", "twentieth"];
  return ordinals[number];
}

export async function getPaymentDialog(containerDoc, header, message, parent, plan, section, student, utc) {
  let result;
  let dialog = document.createElement('div');
  dialog.classList.add('dialog', 'ui');
  dialog.appendChild(getHeader(header, dialog));
  let dialogMessage = document.createElement('div');
  dialogMessage.classList.add('dialog-text');
  dialogMessage.innerHTML = message;
  dialog.appendChild(dialogMessage);
  let div = document.createElement('div');
  div.id = `paypal-button-container-${plan}`;
  div.style.height = '127px';
  div.style.overflowY = 'clip';
  div.style.width = '100%';
  dialog.appendChild(div);
  let dialogOverlay = document.createElement('div');
  dialogOverlay.classList.add('overlay', 'dialog');
  dialogOverlay.appendChild(dialog);
  containerDoc.body.appendChild(dialogOverlay);
  let openedAt = Date.now();
  act(dialogOverlay, 'click', (event) => {
    if (Date.now() - openedAt < 300) return;
    if (event.target.closest('.dialog.ui')) return;
    dialogOverlay.remove();
    result = 'cancelled';
  });
  let script = document.createElement('script');
  script.src = 'https://www.paypal.com/sdk/js?client-id=AUA0E-TX9o2PtTCqejCDEkKKtg77OBTCOJ8yTFc3Kk4cWrEjKFUc1gZX9Rte2oeueo2BNDzmOKApRnGe&vault=true&intent=subscription';
  script.setAttribute('data-sdk-integration-source', 'button-factory');
  script.onload = () => {
    paypal.Buttons({
      style: {
          shape: 'rect',
          color: 'blue',
          layout: 'vertical',
          label: 'subscribe'
      },
      createSubscription: async function(data, actions) {
        return actions.subscription.create({
          custom_id: `${parent}-${section}-${student}`,
          plan_id: plan,
          start_time: utc
        });
      },
      onApprove: async function(data, actions) {
        result = 'approved';
        dialogOverlay.remove();
      }
    }).render(`#paypal-button-container-${plan}`); // Renders the PayPal button
  }
  document.head.appendChild(script);
  while (!result) await sleep(100);
  return result;
}

export async function getPopup(container, keyData, prefix, score, func, timer) {
  if (!container) return;
  let popup = container.querySelector('.popup');
  if (!popup) {
    popup = document.createElement('div');
    popup.classList.add('popup');
    popup.id = 'popup';
    act(popup, 'click', () => popup.func?.());
    container.appendChild(popup);
  }
  popup.func = null;
  let message = popup.querySelector('#popup-message') || document.createElement('span');
  message.id = 'popup-message';
  if (func && score === 'check') {
    popup.innerHTML = z.svg.icons.zebulaicon;
    message.innerHTML = 'Click here to check your answer.';
    if (!popup.contains(message)) popup.appendChild(message);
    popup.style.cursor = 'pointer';
    popup.style.display = 'flex';
    popup.func = func;
  } else if (func) {
    popup.innerHTML = z.svg.icons.info;
    message.innerHTML = 'Click here for feedback.';
    if (!popup.contains(message)) popup.appendChild(message);
    popup.style.cursor = 'pointer';
    popup.style.display = 'flex';
    popup.func = func;
  } else if (String(score).includes('hecking')) {
    popup.innerHTML = z.svg.icons.zebulaicon;
    message.innerHTML = score;
    if (!popup.contains(message)) popup.appendChild(message);
    popup.style.cursor = 'default';
    popup.style.display = 'flex';
    return;
  } else if (!score && score !== 0) {
    message.innerHTML = keyData;
    if (timer) {
      let timerBar = document.createElement('div');
      timerBar.classList.add('popup-timer-bar');
      timerBar.style.setProperty('--timer', `${timer}ms`);
      popup.appendChild(timerBar);
      setTimeout(() => {
        popup.style.display = 'none';
        timerBar.remove();
      }, timer);
    }
    if (!popup.contains(message)) popup.appendChild(message);
    popup.style.display = 'flex';
  } else if (score === -1) {
    if (!keyData?.[`answer${prefix}_0%Message`]) popup.style.display = 'none';
    else {
      popup.innerHTML = null;
      message.innerHTML = keyData[`answer${prefix}_0%Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
    }
  } else if (score === 0) {
    if (!keyData?.[`answer${prefix}_Message`]) popup.style.display = 'none';
    else {
      popup.innerHTML = null;
      message.innerHTML = keyData[`answer${prefix}_Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
    }
  } else if (score === 0.25) {
    if (!keyData?.[`answer${prefix}_25%Message`]) popup.style.display = 'none';
    else {
      popup.innerHTML = null;
      message.innerHTML = keyData[`answer${prefix}_25%Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
    }
  } else if (score === 0.5) {
    if (!keyData?.[`answer${prefix}_50%Message`]) popup.style.display = 'none';
    else {
      popup.innerHTML = null;
      message.innerHTML = keyData[`answer${prefix}_50%Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
    }
  } else if ([0.75, '1*'].includes(score)) {
    if (!keyData?.[`answer${prefix}_75%Message`]) popup.style.display = 'none';
    else {
      popup.innerHTML = null;
      popup.innerHTML = keyData[`answer${prefix}_75%Message`] === 'Correct! Simplify to improve.' ? z.svg.icons.asterisk : '';
      message.innerHTML = keyData[`answer${prefix}_75%Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
    }
  } else if (score === 1) {
    if (!keyData?.[`answer${prefix}_100%Message`] && !container.classList.contains('math-div')) popup.style.display = 'none';
    else {
      popup.innerHTML = z.svg.icons.checkcircle;
      message.innerHTML = container.classList.contains('math-div') ? 'Correct!' : keyData[`answer${prefix}_100%Message`];
      popup.appendChild(message);
      popup.style.display = 'flex';
      // A screen may hold its "Correct!" for less (the steps screen, slide.js POPUP_HOLD_MS).
      setTimeout(() => popup.style.opacity = 0, Number(container.closest('[data-popup-hold]')?.dataset.popupHold) || 2000);
    }
  } else popup.style.display = 'none';
}

export function getPreview(obj, icon) { try {
  let preview = [];
  let previewOverlay = document.createElement('div');
  previewOverlay.classList.add('overlay');
  let previewContainer = document.createElement('div');
  previewContainer.classList.add('preview-container');
  if (obj.canvas) for (let i = 1; i < obj.canvas.length; i++) {
      if (!preview[i]) {
        preview[i] = document.createElement('div');
        preview[i].classList.add('preview');
        previewContainer.appendChild(preview[i]);
        previewOverlay.appendChild(previewContainer);
        act(previewOverlay, 'click', (event) => event.target === previewOverlay ? previewOverlay.remove() : event.stopPropagation());
      }
      preview[i].appendChild(obj.canvas[i]);
      let copyButton = document.createElement('button');
      copyButton.classList.add('copy-button');
      copyButton.innerHTML = 'Copy';
      preview[i].appendChild(copyButton);
      act(copyButton, 'click', () => obj.canvas[i].toBlob(blob => { navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])}, 'image/png'));
      let downloadButton = document.createElement('button');
      downloadButton.classList.add('download-button');
      downloadButton.innerHTML = 'Download';
      preview[i].appendChild(downloadButton);
      act(downloadButton, 'click', () => {
        obj.canvas[i].toBlob(blob => {
          let url = URL.createObjectURL(blob);
          let a = document.createElement('a');
          a.href = url;
          a.download = `${obj.canvas[i].id}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 'image/png');
      });
    }
  if (obj.text) for (let i = 1; i < obj.text.length; i++) {
    if (!preview[i]) {
      preview[i] = document.createElement('div');
      preview[i].classList.add('preview');
      previewContainer.appendChild(preview[i]);
      previewOverlay.appendChild(previewContainer);
      act(previewOverlay, 'click', (event) => event.target === previewOverlay ? previewOverlay.remove() : event.stopPropagation());
    }
    if (obj.text[i]) preview[i].appendChild(obj.text[i]);
    // Sparkles, left of Copy. Only on cards that declare a single code (the phase memos) —
    // the progress and checklist cards span the whole term, where per-problem observations
    // stop being observations. `obj.write` is the generator, supplied by the caller.
    if (obj.write && obj.text[i]?.hasAttribute?.('data-code')) {
      let aiButton = document.createElement('button');
      aiButton.classList.add('preview-ai-button');
      aiButton.innerHTML = icon.sparkles;
      aiButton.title = 'Generate insights';
      // `empty` rather than `disabled` for a memo that came back with nothing: a disabled
      // button receives no hover in Chrome, so its title never appears, and the title is where
      // the teacher's read-out of WHY it came back empty lives. Greyed and inert, still
      // hoverable, and the guard here is what the disabled attribute was doing.
      act(aiButton, 'click', () => { if (!aiButton.classList.contains('empty')) obj.write(obj.text[i]); });
      preview[i].appendChild(aiButton);
    }
    // Confirmation is a PULSE, not a label swap: "Copied!" is wider than "Copy", so the button
    // grew leftwards over the sparkle sitting beside it. Reusing the sparkle's own generating
    // animation holds the width still and makes the two controls read as one family.
    const pulse = (button) => {
      button.classList.remove('copied');
      void button.offsetWidth;
      button.classList.add('copied');
      setTimeout(() => button.classList.remove('copied'), 600);
    };
    let copyTextButton = document.createElement('button');
    copyTextButton.classList.add('copy-text-button');
    copyTextButton.innerHTML = 'Copy';
    preview[i].appendChild(copyTextButton);
    // What the card says MINUS anything marked as a label for the teacher. The memo header
    // ("Lesson progress based on half the class average") describes what the card measures and
    // is there for whoever is flipping through the pages; it is not part of the letter and must
    // not travel into the clipboard. Cloned rather than hidden, since hiding it would take it
    // off the screen too, and innerText is what skips unrendered nodes.
    act(copyTextButton, 'click', async () => {
      try {
        let text = obj.text[i]?.innerText || obj.text[i]?.innerHTML?.replace(/<br\s*\/?>/gi, '\n') || '';
        await navigator.clipboard.writeText(text);
        pulse(copyTextButton);
      } catch (error) {
        // Fallback for browsers that block clipboard API
        let textarea = document.createElement('textarea');
        textarea.value = obj.text[i]?.innerText || '';
        textarea.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
        pulse(copyTextButton);
      }
    });
  }
  // What the card in front of you measures, in its own floating box between the card and the
  // page count. Deliberately OUTSIDE the card: it is a label for whoever is flipping through
  // the pages, not a line of the letter, and anything inside the card is lifted by Copy and
  // sent home. Carried on the card as data-header so this stays a caption renderer and the
  // wording lives with the cards themselves; hidden on any card without one.
  let previewHeader = document.createElement('div');
  previewHeader.classList.add('preview-header', 'ui');
  const setHeader = (index) => {
    let header = obj.text?.[index]?.getAttribute?.('data-header') || '';
    previewHeader.innerHTML = header;
    previewHeader.style.display = header ? '' : 'none';
  };
  if (preview.length > 1) {
    previewContainer.appendChild(previewHeader);
    let previewNavigation = document.createElement('div');
    previewNavigation.classList.add('preview-navigation');
    let previewPrevious = document.createElement('button');
    previewPrevious.classList.add('preview-previous');
    previewPrevious.innerHTML = icon.back;
    previewNavigation.appendChild(previewPrevious);
    let previewIndex = 1;
    let previewCount = document.createElement('div');
    previewCount.classList.add('preview-count', 'ui');
    previewCount.innerHTML = `${previewIndex} / ${preview.length - 1}`;
    setHeader(previewIndex);
    previewContainer.appendChild(previewCount);
    let previewNext = document.createElement('button');
    previewNext.classList.add('preview-next');
    previewNext.innerHTML = icon.next;
    previewNavigation.appendChild(previewNext);
    previewContainer.appendChild(previewNavigation);
    preview[previewIndex].style.display = 'block';
    act(previewPrevious, 'click', () => {
      previewIndex--;
      if (previewIndex < 1) previewIndex = preview.length - 1;
      previewCount.innerHTML = `${previewIndex} / ${preview.length - 1}`;
      setHeader(previewIndex);
      preview.forEach(preview => preview.style.display = 'none');
      preview[previewIndex].style.display = 'block';
    });
    act(previewNext, 'click', () => {
      previewIndex++;
      if (previewIndex > preview.length - 1) previewIndex = 1;
      previewCount.innerHTML = `${previewIndex} / ${preview.length - 1}`;
      setHeader(previewIndex);
      preview.forEach(preview => preview.style.display = 'none');
      preview[previewIndex].style.display = 'block';
    });
  }
  document.body.appendChild(previewOverlay);
} catch (error) { logError(error, 'getting preview'); }}

export function getRandomString(length) {
  let chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let randomString = '';
  for (let i = 0; i < length; i++) randomString += chars.charAt(Math.floor(Math.random() * chars.length));
  return randomString;
}

// Display a unix-epoch timestamp in the teacher's chosen time zone (falls back
// to the browser's local zone). Stored data stays unix epoch / UTC; this
// function just chooses how to render it.
export function getTimestamp(unixTime) {
  const tz = z.teachers?.[z.teacher]?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(unixTime);
  const today = new Date();
  // Pull the parts in the chosen tz so date comparisons + the `month/day/year`
  // string format reflect that zone, not the browser's.
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).formatToParts(date);
  const get = type => parts.find(p => p.type === type)?.value;
  const todayParts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(today);
  const tGet = type => todayParts.find(p => p.type === type)?.value;
  const hours = get('hour');
  const minutes = get('minute');
  const dayPeriod = get('dayPeriod');
  if (get('day') === tGet('day') && get('month') === tGet('month') && get('year') === tGet('year')) return `${hours}:${minutes} ${dayPeriod}`;
  return `${get('month')}/${get('day')}/${get('year')} ${hours}:${minutes} ${dayPeriod}`;
}

export function getUTC(date) {
  let year = date.getUTCFullYear();
  let month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

// Is this roster profile locked out of the site? Two ways in: the teacher set Status to
// 'locked' outright, or they scheduled `lockDate` and that day has arrived. The scheduled
// form is evaluated HERE rather than flipped by a background job, so the lock lands on the
// day the teacher chose whether or not anyone was online to flip it — and picking any
// status in the settings dialog clears the pending date, so the two can never disagree.
// Takes the profile rather than a uid because callers already hold it and it lives in
// EITHER bucket (/roster/ or /new/) — a teacher can lock an account before its first login.
export function isLocked(profile) {
  if (!profile) return false;
  if (profile.status === 'locked') return true;
  if (!profile.lockDate) return false;
  // Compared as plain YYYY-MM-DD strings, which sort lexicographically and keep the
  // boundary on the viewer's own calendar day. Parsing them into Dates would put it at
  // UTC midnight instead, locking a student a day early or late depending on their offset.
  let now = new Date();
  return profile.lockDate <= `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function isNumber(string) {
  return !isNaN(parseFloat(string)) && isFinite(string);
}

export async function length(obj) {
  let count = 0;
  for (let key in obj) count++;
  return count;
}

export async function logError(error, context) { try {
  console.error(error);
  // Offline mode: the Cloud Function call below would internally try to touch
  // RTDB. Skip it entirely so offline mode is verifiably zero-RTDB-touch.
  if (OFFLINE) return;
  // Coerce every non-Error shape into an Error so the server reliably gets a stack.
  // Plain-object / null / undefined throws used to reach the server with stack=undefined,
  // which the functions-side parser couldn't split — dropping the trace entirely.
  // When we can't recover the original throw site, a stack captured HERE (at the logError
  // call) is still strictly better than nothing: it names the catch block calling us.
  if (!(error instanceof Error)) {
    let msg = typeof error === 'string' ? error : (error?.message || JSON.stringify(error));
    error = new Error(msg);
  }
  if (!error.stack) Error.captureStackTrace?.(error, logError);
  let params = `&app=${z.app  || 'null'}&code=${z.code || 'null'}&session=${z.session?.key || 'null'}&student=${z.student || 'null'}&teacher=${z.teacher || 'null'}&tooltip=${navigator.userAgent}&type=error&version=${process.env.VERSION}`;
  if (prevError === error.message && prevParams === params) return;
  let logError = httpsCallable(window.firebaseFunctions, 'logError');
  logError({ context: context, stack: error.stack, message: error.message, name: z.name || 'Someone', params: params, teacher: z.teacher });
  [prevError, prevParams] = [error.message, params];
  // Stale-client recovery. After a deploy the previous bundle's chunks are gone, so the next
  // dynamic import in a still-open session 404s. index.js recovers from that via versionReload
  // — but only for UNCAUGHT rejections, and every `await import()` here sits inside a try/catch,
  // so the recovery was unreachable from the one place it actually happens (observed: three
  // students stuck on a dead slide/avatar listener until they refreshed). Fires after the log
  // dispatch above so the entry still reaches the dashboard, and versionReload's own
  // once-per-version sessionStorage guard prevents a reload loop.
  if (/Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i.test(error.message || '')) versionReload();
} catch (error) { console.error(error, context); }}

export async function logMessage(message, data = {}) {
  try {
    if (z.student && await safeGet(`/students/${z.student}/settings/log`) === false) return;
    let entry = {
      message: message,
      ...data
    };
    // Firebase rejects an ENTIRE push whose value holds an undefined property, so one
    // absent field silently discarded the whole log entry (and logged an error in its
    // place). The failed-login path is the common case — it reports `student: z.student`
    // before the student has ever authenticated, so z.student is undefined.
    for (let field in entry) if (entry[field] === undefined) delete entry[field];
    // A client push needs auth — the root rule is `.write: "auth != null"` — and the most
    // valuable message routed through here is written at the exact moment there ISN'T any:
    // index.js fail() reports a bad student login right after signInWithEmailAndPassword
    // rejected. So "X attempted to connect with the code C, the color Y, and the Z emoji"
    // was denied every single time and never reached a teacher's log (0 occurrences in 3000
    // records, while 1103 ordinary connection entries in the same sample landed fine); the
    // `pushing to /teachers/{id}/log: PERMISSION_DENIED` errors ARE this write failing.
    // Fall back to the logError CF, which writes with admin privileges and needs no session.
    // `plain: true` is what stops it wrapping the text in its "encountered an error"
    // template; params keep the &type=connection tagging the dashboard renderer parses, so
    // the entry lands in the log looking exactly like an authenticated one.
    if (!auth?.currentUser) {
      let params = '';
      for (let field in entry) if (field !== 'message') params += `&${field}=${entry[field] ?? 'null'}`;
      httpsCallable(window.firebaseFunctions, 'logError')({ message, params, plain: true, teacher: z.teacher || 'null' });
      return;
    }
    if (z.teacher) safePush(`/teachers/${z.teacher}/log`, entry);
    else safePush(`/admin/log`, entry);
  } catch (error) {
    console.error(`Error logging message: ${error}`);
  }
}

function read(obj, path) {
  return path.reduce((node, key) => { return node?.[key] }, obj) ?? {};
}

// A bonus code (shsat18a) is a lesson made outside the sheet. Its records are shaped exactly like
// a sheet lesson's but live under /bonus — /bonus/codes/{code}, /bonus/apps/{app}/{code} and
// /bonus/apps/keys/{key} — where a sheet upload can never overwrite them. Only the DATABASE path
// moves: z still holds them at z.codes and z.apps like any other lesson, so no reader has to
// know. A bonus code is named after its document (shsat18a), so nothing can be read off the
// name: the codes are whatever /bonus/codes lists, read once before the first database read,
// and each carries its own grade. A bonus key is 8 digits (a sheet key is 7).
let bonus = null;
let bonusLoading = null;

function loadBonus() {
  bonusLoading ??= get(ref(database, '/bonus/codes')).then(snapshot => bonus = snapshot.val() || {}).catch(() => bonus = {});
  return bonusLoading;
}

export function bonusCode(code) {
  return !!bonus?.[code];
}

// The grade a code belongs to: a sheet code's fourth character (mat516), a bonus code's own record.
export function codeGrade(code) {
  return String(bonus?.[code]?.grade ?? String(code ?? '').charAt(3));
}

// What a code is called in a list: a sheet code's topic from its grade and lesson number
// (mat516), a bonus code's own title.
export function codeTitle(code) {
  return bonus?.[code]?.title ?? z.topics?.[String(code ?? '').charAt(3)]?.[parseInt(String(code ?? '').substring(4)) - 1] ?? code;
}

function source(path, safeQuery) {
  let [root, node, child] = normPath(path).split('/');
  let bonus = false;
  if (root === 'codes') bonus = bonusCode(node);
  else if (root === 'apps' && node === 'keys') bonus = child ? /^\d{8}$/.test(child) : [safeQuery].flat().some(clause => bonusCode(clause?.val));
  else if (root === 'apps') bonus = bonusCode(child);
  return bonus ? `/bonus/${normPath(path)}` : path;
}

export async function safeGet(safeRef, options = {}, context) {
  context = context ?? `reading ${safeRef}`;
  if (OFFLINE) return getOffline(safeRef, options);
  if (!bonus) await loadBonus();
  // options: { query, static, cache } — fetch behavior config
  // - query: Firebase query descriptor (e.g. { query: { func: 'orderByChild', child: 'foo', val: 'bar' } })
  //          or { query: { func: 'limitToFirst', val: 100 } } — same shape as the old safeQuery arg
  // - static: true → skip the network if data is already in z (curriculum/icons/etc.)
  // - cache: false → return snapshot value WITHOUT writing it to z. Use for query results
  //          on shared-parent paths so the filtered subset doesn't clobber sibling data
  //          already cached at the parent. Default (cache: true / undefined) writes to z.
  // context: optional string for error-message logging (kept separate since it's call metadata,
  //          not behavior config)
  const { query: safeQuery, static: isStatic, cache } = options;
  // Static data: skip the network entirely if it's already in z. Curriculum/icon/code-table
  // data doesn't change during a class period, so the first fetch is the only one we need.
  // Only applies to non-queried reads — query results are filtered subsets, not the same
  // shape as a direct path read.
  if (isStatic && !safeQuery) {
    let path = safeRef.split('/').slice(1);
    let cached = read(z, path);
    if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) return cached;
    // Primitive (string/number/boolean) cache: cached !== {} (read's empty fallback)
    if (cached !== undefined && cached !== null && typeof cached !== 'object') return cached;
  }
  const maxRetries = 3;
  const timeoutMs = 15000; // 15 second timeout per attempt

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      let path = safeRef.split('/').slice(1);
      if (!student && safeRef.includes('students')) student = safeRef.split('/')[2];
      else if (!teacher && safeRef.includes('teachers')) teacher = safeRef.split('/')[2];
      
      // Add timeout wrapper to prevent indefinite hangs on poor network conditions
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error(`Database read timed out after ${timeoutMs}ms for path: ${safeRef}`)), timeoutMs)
      );
      
      let getPromise;
      const dbRef = ref(database, source(safeRef, safeQuery));
      if (safeQuery) {
        if (safeQuery.func === 'orderByChild') {
          getPromise = get(query(dbRef, orderByChild(safeQuery.child), equalTo(safeQuery.val)));
        } else {
          getPromise = get(query(dbRef, safeQuery.func === 'limitToFirst' ? limitToFirst(safeQuery.val) : limitToLast(safeQuery.val)));
        }
      } else {
        getPromise = get(dbRef);
      }

      const snapshot = await Promise.race([getPromise, timeoutPromise]);
      if (cache === false) return snapshot.val();
      let val = (await write(z, path, snapshot.val(), context ?? 'getting data')).val[path.length];
      // A whole-app read (/apps/math) lists every lesson of that app, so its bonus codes are
      // read in beside the sheet's — the roster report and the code totals walk that list.
      if (!safeQuery && path.length === 2 && path[0] === 'apps' && !['keys', 'rules'].includes(path[1])) {
        const codes = (await Promise.race([get(ref(database, `/bonus/apps/${path[1]}`)), timeoutPromise])).val() || {};
        for (const code in codes) await write(z, [...path, code], codes[code], context ?? 'getting data');
        val = read(z, path);
      }
      return val;
      
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      
      if (isLastAttempt) {
        // Final attempt failed - log error and throw
        logError(error, context);
        throw error; // Re-throw to let calling code handle it
      } else {
        // Retry with exponential backoff
        const delayMs = Math.min(1000 * Math.pow(2, attempt), 8000); // Cap at 8 seconds
        console.warn(`Database read attempt ${attempt + 1} failed for ${safeRef}. Retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }
}

export async function safeOff(safeRef, context) {
  context = context ?? `detaching a listener on ${safeRef}`;
  try {
    if (OFFLINE) return;
    off(ref(database, safeRef));
    // Also clean up any z.subs entries for this path so the dedup layer doesn't
    // treat subsequent subscribes as "already subscribed" on a dead Firebase listener.
    if (z.subs) {
      const np = normPath(safeRef);
      for (const key of Object.keys(z.subs)) {
        if (key.includes(`|${np}|`)) {
          z.subs[key].off = null;
          z.subs[key].callbacks.clear();
          delete z.subs[key];
        }
      }
    }
  } catch (error) { logError(error, context); }
}

// ========================================================================
// Shared subscription layer.
// One real Firebase subscription per unique (type, path, queryHash);
// multiple callers fan out from a shared writeFn + user-callback dispatch.
// The shared writeFn updates the z cache ONCE per event regardless of how
// many callers are subscribed. User callbacks fan out to each subscriber.
// Late subscribers replay from cache (child_added) or prev snapshot (value).
// Registry lives on z.subs; keyed by `${type}|${normPath}|${queryHash}`.
// ========================================================================

function normPath(p) { return !p ? '' : (p.startsWith('/') ? p.slice(1) : p); }
function qHash(q) { if (!q) return ''; try { return JSON.stringify(q); } catch { return '?'; } }

function buildDbQuery(dbRef, safeQuery) {
  if (!safeQuery) return dbRef;
  if (Array.isArray(safeQuery)) {
    const cs = [];
    for (const c of safeQuery) {
      if (c.func === 'orderByChild')      cs.push(orderByChild(c.val));
      else if (c.func === 'orderByKey')   cs.push(orderByKey());
      else if (c.func === 'equalTo')      cs.push(equalTo(c.val));
      else if (c.func === 'startAt')      cs.push(startAt(c.val));
      else if (c.func === 'endAt')        cs.push(endAt(c.val));
      else if (c.func === 'limitToFirst') cs.push(limitToFirst(c.val));
      else if (c.func === 'limitToLast')  cs.push(limitToLast(c.val));
    }
    return query(dbRef, ...cs);
  }
  if (safeQuery.func === 'limitToFirst') return query(dbRef, limitToFirst(safeQuery.val));
  if (safeQuery.func === 'limitToLast')  return query(dbRef, limitToLast(safeQuery.val));
  return dbRef;
}

function subscribe({ type, path, safeQuery, writeFn, userFunc, initialReplay }) {
  if (OFFLINE) return subscribeOffline({ type, path, writeFn, userFunc, initialReplay });
  z.subs = z.subs || {};
  const np = normPath(path);
  const qh = qHash(safeQuery);
  const key = `${type}|${np}|${qh}`;
  const id = Symbol();
  let entry = z.subs[key];
  const isNew = !entry;
  if (isNew) {
    entry = { type, np, qh, callbacks: new Map(), off: null };
    if (initialReplay === 'cache') entry.cache = new Map();
    if (initialReplay === 'prev')  entry.prev = null;
    z.subs[key] = entry;
  }
  // CRITICAL: add this caller's callback to the map BEFORE attaching to Firebase.
  // Firebase delivers initial state synchronously during onValue/onChildAdded
  // — if we attached first, the very first snapshot would fan out to an empty
  // callback map and the first subscriber would silently never receive it.
  entry.callbacks.set(id, userFunc);
  if (isNew) {
    const fbHandler = (snapshot) => {
      let extra;
      try { extra = writeFn ? writeFn(snapshot) : undefined; }
      catch (e) { logError(e, `shared subscription writeFn (${key})`); }
      // Keep companion child_added cache in sync for the same path+query
      if (type === 'child_removed') {
        const sib = z.subs[`child_added|${np}|${qh}`];
        if (sib?.cache) sib.cache.delete(snapshot.key);
      } else if (type === 'child_changed') {
        const sib = z.subs[`child_added|${np}|${qh}`];
        if (sib?.cache?.has(snapshot.key)) sib.cache.set(snapshot.key, { snapshot, extra });
      }
      if (initialReplay === 'cache') entry.cache.set(snapshot.key, { snapshot, extra });
      if (initialReplay === 'prev')  entry.prev = { snapshot, extra };
      // Snapshot callbacks to an array so mid-dispatch mutations don't corrupt iteration.
      // Presence re-check matches DOM removeEventListener semantics (removed-mid-dispatch → skip).
      const toFire = [...entry.callbacks.entries()];
      for (const [cid, cb] of toFire) {
        if (!entry.callbacks.has(cid) || typeof cb !== 'function') continue;
        try { cb(snapshot, extra); }
        catch (e) { logError(e, `shared subscriber callback (${key})`); }
      }
    };
    try {
      const dbRef = ref(database, source(path, safeQuery));
      const dbQuery = buildDbQuery(dbRef, safeQuery);
      if (type === 'value')              entry.off = onValue(dbQuery, fbHandler);
      else if (type === 'child_added')   entry.off = onChildAdded(dbQuery, fbHandler);
      else if (type === 'child_changed') entry.off = onChildChanged(dbQuery, fbHandler);
      else if (type === 'child_removed') entry.off = onChildRemoved(dbQuery, fbHandler);
    } catch (e) {
      logError(e, `shared subscription attach (${key})`);
      entry.callbacks.delete(id);
      delete z.subs[key];
      return () => {};
    }
  } else if (userFunc && typeof userFunc === 'function') {
    // Late subscriber: synthesize initial state from what the shared subscription
    // has already cached. (First subscriber gets initial via Firebase's own delivery.)
    if (initialReplay === 'cache' && entry.cache) {
      for (const { snapshot, extra } of entry.cache.values()) {
        try { userFunc(snapshot, extra); }
        catch (e) { logError(e, `late subscriber initial replay (${key})`); }
      }
    }
    if (initialReplay === 'prev' && entry.prev) {
      try { userFunc(entry.prev.snapshot, entry.prev.extra); }
      catch (e) { logError(e, `late subscriber initial replay (${key})`); }
    }
  }
  return function off() {
    const e = z.subs?.[key];
    if (!e) return;
    e.callbacks.delete(id);
    if (e.callbacks.size === 0) {
      try { e.off?.(); }
      catch (err) { logError(err, `shared subscription detach (${key})`); }
      e.off = null;
      delete z.subs[key];
    }
  };
}

// Peek at z.subs to detect whether a shared subscription still has live callbacks.
// Lets consumers (e.g. game.js unloadChunk) clean up shared cache state ONLY when
// they are the last subscriber — without the check, a per-instance unload would
// purge entries other Game instances (dashboard tiles) still render from.
export function hasSubs(type, path, safeQuery) {
  if (!z.subs) return false;
  return !!z.subs[`${type}|${normPath(path)}|${qHash(safeQuery)}`];
}

// Shared across child_* subscribes. For queries that filter by chunk, Firebase's
// multi-path writes can deliver partial snapshots (just the fields that matched the
// query, not the full entity). The chunk-aware writeFns merge with existing z data
// to preserve the full record, and child_removed skips erasure when the entity moved
// chunks instead of being truly deleted.
function isChunkSafeQuery(safeQuery) {
  return safeQuery?.some?.(q => q.func === 'orderByChild' && q.val === 'chunk');
}

export function safeOnChildAdded(safeRef, func, context) {
  context = context ?? `listening for children added to ${safeRef}`;
  try {
    let safePath, safeQuery;
    if (typeof safeRef === 'string') { safePath = safeRef; safeQuery = null; }
    else { safePath = safeRef.path; safeQuery = safeRef.query; }
    const isChunkQuery = isChunkSafeQuery(safeQuery);
    const writeFn = (snapshot) => {
      const itemPath = [...safePath.split('/').slice(1), snapshot.key];
      const snapshotVal = snapshot.val();
      if (isChunkQuery) {
        const existing = read(z, itemPath);
        if (existing && typeof existing === 'object' && Object.keys(existing).length > Object.keys(snapshotVal || {}).length) {
          write(z, itemPath, Object.assign({}, existing, snapshotVal), context ?? 'adding child data');
          return;
        }
      }
      write(z, itemPath, snapshotVal, context ?? 'adding child data');
    };
    return subscribe({ type: 'child_added', path: safePath, safeQuery, writeFn, userFunc: func, initialReplay: 'cache' });
  } catch (error) { logError(error, context); }
}

export function safeOnChildChanged(safeRef, func, context) {
  context = context ?? `listening for children changed on ${safeRef}`;
  try {
    let safePath, safeQuery;
    if (typeof safeRef === 'string') { safePath = safeRef; safeQuery = null; }
    else { safePath = safeRef.path; safeQuery = safeRef.query; }
    const isChunkQuery = isChunkSafeQuery(safeQuery);
    const writeFn = (snapshot) => {
      const parentPath = safePath.split('/').slice(1);
      const itemPath = [...parentPath, snapshot.key];
      const beforeValue = read(z, parentPath)?.[snapshot.key] ?? null;
      const snapshotVal = snapshot.val();
      if (isChunkQuery && beforeValue && typeof beforeValue === 'object' && Object.keys(beforeValue).length > Object.keys(snapshotVal || {}).length) {
        write(z, itemPath, Object.assign({}, beforeValue, snapshotVal), context ?? 'changing child data');
      } else {
        write(z, itemPath, snapshotVal, context ?? 'changing child data');
      }
      return beforeValue;
    };
    return subscribe({ type: 'child_changed', path: safePath, safeQuery, writeFn, userFunc: func, initialReplay: null });
  } catch (error) { logError(error, context); }
}

export function safeOnChildRemoved(safeRef, func, context) {
  context = context ?? `listening for children removed from ${safeRef}`;
  try {
    let safePath, safeQuery;
    if (typeof safeRef === 'string') { safePath = safeRef; safeQuery = null; }
    else { safePath = safeRef.path; safeQuery = safeRef.query; }
    const isChunkQuery = isChunkSafeQuery(safeQuery);
    const writeFn = (snapshot) => {
      const itemPath = [...safePath.split('/').slice(1), snapshot.key];
      if (isChunkQuery) {
        const queryChunk = safeQuery.find(q => q.func === 'equalTo')?.val;
        const cachedChunk = read(z, itemPath)?.chunk;
        const snapshotChunk = snapshot.val()?.chunk;
        // Entity left this chunk query — moved to another chunk or truly deleted.
        // If EITHER cached or snapshot chunk differs from queryChunk, it moved;
        // don't erase (the new chunk's subscription will manage it).
        if (queryChunk && ((cachedChunk && cachedChunk !== queryChunk) || (snapshotChunk && snapshotChunk !== queryChunk))) return;
      }
      erase(z, itemPath);
    };
    return subscribe({ type: 'child_removed', path: safePath, safeQuery, writeFn, userFunc: func, initialReplay: null });
  } catch (error) { logError(error, context); }
}

export async function safeOnDisconnect(safeRef, context) {
  context = context ?? `setting an on-disconnect for ${safeRef}`; try {
  if (OFFLINE) return;
  onDisconnect(ref(database, safeRef)).remove();
} catch (error) { logError(error, context); }}

export async function safeCancelOnDisconnect(safeRef, context) {
  context = context ?? `cancelling the on-disconnect for ${safeRef}`; try {
  if (OFFLINE) return;
  await onDisconnect(ref(database, safeRef)).cancel();
} catch (error) { logError(error, context); }}

export function safeOnValue(safeRef, func, context) {
  context = context ?? `listening to ${safeRef}`; try {
  const writeFn = (snapshot) => {
    const path = safeRef.split('/').slice(1);
    const before = JSON.parse(JSON.stringify(read(z, path)));
    const val = snapshot.val();
    if (snapshot.exists()) write(z, path, val, context ?? 'reading data on value change');
    else erase(z, path);
    return before;
  };
  return subscribe({ type: 'value', path: safeRef, safeQuery: null, writeFn, userFunc: func, initialReplay: 'prev' });
} catch (error) { logError(error, context); }}

export async function safePush(safeRef, val, context) {
  context = context ?? `pushing to ${safeRef}`;
  try {
    if (OFFLINE) return await pushOffline(safeRef, val);
    let result = await push(ref(database, safeRef), val);
    return { key: result.key, ref: `${safeRef}/${result.key}`, val: val };
  } catch (error) { logError(error, context);
  } finally { if (!OFFLINE) timestampSession(); }
}

// QUEUED DIALOGS: a message the player has to read, kept at /students/{s}/game/dialogs/{pushKey}
// until its button is pressed (Game.showDialogs). Push keys sort in the order they were queued, so
// several show one after another, oldest first. The VM queues the same shape (a critter's delivery,
// a finished bed or door); anything client-side that needs one calls this.
export function queueDialog(student, header, message, button) {
  if (!student || !message) return;
  return safePush(`/students/${student}/game/dialogs`, { header: header ?? null, message, button: button ?? null, time: Date.now() }, 'queueing a dialog');
}

export async function safeRemove(safeRef, context) {
  context = context ?? `removing ${safeRef}`;
  try {
    if (OFFLINE) { removeOffline(safeRef); return; }
    let path = safeRef.split('/').slice(1);
    remove(ref(database, safeRef));
    erase(z, path);
  } catch (error) { logError(error, context); }
}

// The teacher's Bonus-panel settings (/students/{s}/bonus) as one string, so a bonus problem made
// ahead (gen.js queueBonus) can be checked against the settings in force now (the admin panel).
export const bonusSignature = (settings = {}) => JSON.stringify([settings.prompt || null, settings.doc || null, settings.variety || null, settings.creativity || null, settings.difficulty ?? null, settings.type ?? null, settings.parts ?? null, settings.calculator ?? null]);

// A student's queue of bonus problems made ahead (/students/{s}/bonus/queue/{slot}, gen.js
// queueBonus) as [item, entry] pairs in line order: made items, and items still being made whose
// claim is under 3 minutes old (an older one was left by a page that closed).
export const queueItems = (queue) => Object.entries(queue || {})
  .filter(([, entry]) => entry && (entry.key || Date.now() - (entry.making || 0) < 180000))
  .sort((a, b) => (a[1].order || 0) - (b[1].order || 0));

export async function safeRunTransaction(safeRef, func, context) {
  context = context ?? `running a transaction on ${safeRef}`;
  try {
    if (OFFLINE) return await transactionOffline(safeRef, func);
    let result = await runTransaction(ref(database, safeRef), func);
    // snapshot.val(), not the private `_node.value_`: that field is populated for LEAF nodes only, so
    // a transaction on an object (the critter goal tally) came back undefined. Identical for leaves.
    return result.snapshot.val();
  } catch (error) { logError(error, context); }
}

// Auto-stamp `code` into writes targeting per-key data nodes that we want to query by lesson.
// Matches `/students/{X}/input/{key}` (4 segs), `/students/{X}/stats/keys/{key}` (5 segs),
// and `/teachers/{X}/stats/keys/{key}` (5 segs — class aggregates).
// Source of code: z.apps.keys[key]?.code, which is the static key data already cached.
// Skips if val isn't a mergeable object, val already has code, code isn't known yet, or path
// goes deeper than the key node (e.g., `/input/{key}/{index}` — those preserve code via
// shallow-merge from earlier writes, no stamp needed).
function stampCode(path, val) {
  if (!val || typeof val !== 'object' || val.code !== undefined) return val;
  const segs = path.split('/').filter(Boolean);
  let key;
  if (segs.length === 4 && segs[0] === 'students' && segs[2] === 'input') key = segs[3];
  else if (segs.length === 5 && segs[0] === 'students' && segs[2] === 'stats' && segs[3] === 'keys') key = segs[4];
  else if (segs.length === 5 && segs[0] === 'teachers' && segs[2] === 'stats' && segs[3] === 'keys') key = segs[4];
  if (!key) return val;
  const code = z.apps?.keys?.[key]?.code;
  if (!code) return val;
  return { ...val, code };
}

export async function safeUpdate(safeRef, val, context) {
  context = context ?? `updating ${safeRef}`;
  try {
    if (OFFLINE) return updateOffline(safeRef, val);
    // Handle multi-path updates (when safeRef is '/' and val contains multiple paths)
    if (safeRef === '/' && typeof val === 'object') {
      const transformed = {};
      for (let path in val) transformed[path] = stampCode(path, val[path]);
      update(ref(database, safeRef), transformed);
      // Update local cache for each path in the multi-path update
      for (let path in transformed) {
        let pathArray = path.split('/').slice(1);
        write(z, pathArray, transformed[path], context ?? 'updating data');
      }
      return { key: 'multi-path', ref: safeRef, val: transformed };
    }
    // Handle single-path updates (original behavior)
    const transformed = stampCode(safeRef, val);
    let path = safeRef.split('/').slice(1);
    update(ref(database, safeRef), transformed);
    write(z, path, Object.assign(read(z, path), transformed), context ?? 'updating data');
    return { key: safeRef.split('/').pop(), ref: safeRef, val: transformed };
  } catch (error) { logError(error, context);
  } finally { if (!safeRef.includes('crash')) timestampSession(); }
}

export async function safeSet(safeRef, val, context) {
  context = context ?? `setting ${safeRef}`;
  try {
    if (OFFLINE) return setOffline(safeRef, val);
    let path = safeRef.split('/').slice(1);
    set(ref(database, safeRef), val);
    write(z, path, val, context ?? 'setting data');
    return { key: safeRef.split('/').pop(), ref: safeRef, val: val };
  } catch (error) { logError(error, context);
  } finally { if (!OFFLINE) timestampSession(); }
}

export async function sanitize(json) { try {
  // Repair LEGACY/malformed history records only. Everything written by the current code
  // goes through JSON.stringify and is already valid — and the regex chain below actively
  // CORRUPTS a valid record whenever a value legitimately contains a bracket: a student
  // typing "[", or AI feedback carrying LaTeX like \(\frac{4}{3}\). Measured against every
  // stored history record for the students in the error log: 4751/4751 parsed fine as-is,
  // sanitize broke 52 of them and repaired none. Those 52 were the entire population of
  // "Expected ',' or ']'" / "Unterminated string" / "non-whitespace after JSON" errors in
  // updateInputHistory and updateStats — each one losing that answer's history.
  try { JSON.parse(json); return json; } catch (error) {}
  json = json.replace(/^,+/, '').replace(/\[(?!"|\[])/g, '["').replace(/([^"\]])\]/g, '$1"]').replace(/"\[/g, '[').replace(/\]"/g, ']').replace(/\] \[/g, '],[').replace(/^\[(?!\[)/, '[[').replace(/([^\]])\]$/, '$1]]');
  return json;
} catch (error) { logError(error, 'sanitizing a JSON string'); }}

export function select(doc, element) {
  if (element.setSelectionRange && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
    element.setSelectionRange(element.value.length, element.value.length);
  } else if (element.contentEditable === 'true') {
    let range = doc.createRange();
    let selection = doc.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

export function shuffle(array) {
  let output = [...array];
  for (let i = output.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [output[i], output[j]] = [output[j], output[i]];
  }
  return output;
}

export function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function throttle(func, ms) {
  let throttling;
  return function() {
    if (!throttling) {
      func.apply(this, arguments);
      throttling = true;
      setTimeout(() => throttling = false, ms);
    }
  };
}

// The heartbeat. Runs after every write (the finally blocks above) and, on the dashboard, on
// every pointer/key/wheel event (dash.js listen) — a teacher watching tiles writes nothing,
// so activity has to stamp explicitly. `refs` are further session records this activity
// keeps alive: the dashboard passes every teacher-keyed record it owns (pinned codes, report
// tiles), which have no client of their own to stamp them and would otherwise idle out.
// Exported for the dashboard; one 5s gate covers the lot.
export async function timestampSession(refs = []) { try {
  if (!z.session || !z.teacher || !z.uid || !auth.currentUser) return;
  let timestamp = Date.now();
  if (prevTimestamp && Math.abs(timestamp - prevTimestamp) < 5000) return;
  prevTimestamp = timestamp;
  z.active = timestamp;
  let timeUpdate = { active: serverTimestamp() };
  if (!connected) timeUpdate.connected = serverTimestamp();
  await update(ref(database, `/teachers/${z.teacher}/online/${z.session.key}/time`), timeUpdate);
  if (!connected) connected = true;
  for (const path of refs) if (path && path !== z.session.ref) update(ref(database, `${path}/time`), { active: serverTimestamp() }).catch(() => {});
  recordAttendance();
} catch (error) { logError(error, 'timestamping a session'); }}

// ---- Class meetings (the dashboard's Classes) ----
// A class is /teachers/{t}/classes/{key} = { name, days: [0-6, 0 is Sunday], start, end: 'HH:MM',
// students: { uid: true } }, its times clock times in the teacher's time zone. A student's first
// write in a meeting is their attendance: /teachers/{t}/attendance/{class}/{date}/{uid} = the server
// time of that write, the date the teacher's. A write from 5 minutes before the start counts, so
// a student already working when class begins reads as on time.
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const classTimeZone = (teacher) => z.teachers?.[teacher]?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
// The clock in a time zone at a moment: its date (YYYY-MM-DD), weekday (0 is Sunday) and minutes
// since midnight.
export function zonedClock(tz, when = Date.now()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(when).map((p) => [p.type, p.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, day: WEEKDAYS.indexOf(parts.weekday), minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}
// The moment a clock time on a date happens in a time zone.
export function zonedEpoch(date, hhmm, tz) {
  const [y, m, d] = date.split('-').map(Number), [h, mi] = hhmm.split(':').map(Number);
  const offset = (when) => {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(when).find((p) => p.type === 'timeZoneName')?.value || 'GMT';
    const x = name.match(/GMT([+-])(\d+)(?::(\d+))?/);
    return x ? (x[1] === '-' ? -1 : 1) * (Number(x[2]) * 60 + Number(x[3] || 0)) : 0;
  };
  const guess = Date.UTC(y, m - 1, d, h, mi);
  // Twice: the offset is the one in force at the moment itself, which a daylight saving change on
  // that day can make differ from the offset at the guess.
  return guess - offset(guess - offset(guess) * 60000) * 60000;
}
const clockMinutes = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m; };
// The latest meeting of a class that has started by `when`, looking back a week: its date and the
// moments it starts and ends.
export function lastMeeting(data, tz, when = Date.now()) {
  if (!data?.days?.length || !data.start || !data.end) return null;
  for (let back = 0; back < 8; back++) {
    const { date, day } = zonedClock(tz, when - back * 86400000);
    if (!data.days.includes(day)) continue;
    const start = zonedEpoch(date, data.start, tz);
    if (start > when) continue;
    return { date, start, end: zonedEpoch(date, data.end, tz) };
  }
  return null;
}

// Called with every heartbeat of a session (timestampSession), so it runs only on writes the student
// makes. Only on the student's own device (z.student is the signed-in account), only for a class
// listing them, and once per meeting: the record is written only if no device has written it yet.
// The teacher's dashboard (its heartbeat is any activity on it) records the teacher the same way,
// under `teacher`, for every class: a meeting nobody was in, teacher or student, did not happen.
// The classes are read every 10 minutes at most.
let classCache = { time: 0, classes: null };
let zoneCheck = 0;
const attended = new Set();
// The server's clock, from the device's: a student computer whose clock is a few minutes off would
// otherwise miss an announcement's two-minute window or place itself outside the meeting.
let serverOffset = null;
const serverNow = () => {
  if (serverOffset === null) {
    serverOffset = 0;
    onValue(ref(database, '.info/serverTimeOffset'), (snapshot) => { serverOffset = Number(snapshot.val()) || 0; });
  }
  return Date.now() + serverOffset;
};
async function recordAttendance() { try {
  const now = serverNow();
  const uid = auth.currentUser?.uid;
  const who = !uid || !z.teacher ? null : z.student === uid ? uid : z.teacher === uid ? 'teacher' : null;
  if (!who) return;
  if (now - classCache.time > 600000) {
    classCache = { time: now, classes: null };
    classCache.classes = (await get(ref(database, `/teachers/${z.teacher}/classes`))).val();
  }
  // Read on its own clock, not the class cache's: once announcements start the classes are live and
  // the cache never refreshes, so a page open before the zone was saved would never learn it (Oct 28:
  // students outside Pacific time were recorded 28 minutes late, the moment the zone was saved).
  if (!z.teachers?.[z.teacher]?.settings?.timezone && now - zoneCheck > 60000) {
    zoneCheck = now;
    let tz = (await get(ref(database, `/teachers/${z.teacher}/settings/timezone`))).val();
    // Class times are the teacher's clock. With no zone saved, every student's device read them in
    // its own zone, so a student in another one was never marked present and missed every timed
    // announcement. The teacher's own dashboard saves its zone the first time it finds none.
    if (!tz && who === 'teacher') {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await set(ref(database, `/teachers/${z.teacher}/settings/timezone`), tz);
    }
    if (tz) ((z.teachers ??= {})[z.teacher] ??= {}).settings = { ...(z.teachers[z.teacher].settings || {}), timezone: tz };
  }
  if (who !== 'teacher') startAnnouncements(uid);
  // A student's device never guesses the zone: its own would put the meeting at the wrong hour.
  if (who !== 'teacher' && !z.teachers?.[z.teacher]?.settings?.timezone) return;
  const tz = classTimeZone(z.teacher);
  const clock = zonedClock(tz, now);
  for (const [key, data] of Object.entries(classCache.classes || {})) {
    if ((who !== 'teacher' && !data?.students?.[uid]) || !data?.days?.includes(clock.day) || !data.start || !data.end) continue;
    if (clock.minutes < clockMinutes(data.start) - 5 || clock.minutes >= clockMinutes(data.end)) continue;
    const meeting = `${key}/${clock.date}`;
    if (attended.has(meeting)) continue;
    attended.add(meeting);
    const path = `/teachers/${z.teacher}/attendance/${meeting}/${who}`;
    if ((await get(ref(database, path))).val() == null) await set(ref(database, path), serverTimestamp());
  }
} catch (error) { logError(error, 'recording attendance'); }}

// ---- Class announcements ----
// A class's announcements are /teachers/{t}/classes/{key}/announcements/{id} = { message, time:
// 'HH:MM' (absent: on demand only), blocking }. A timed one shows in every meeting at its time, on
// the device of each student in the class: the device keeps the time itself, so nothing has to be
// running on the teacher's side. Sending one now pushes /teachers/{t}/broadcasts/{pushKey} = {
// class, message, blocking }, which every student device in the class shows as it arrives. Blocking
// is a dialog that has to be answered; otherwise a banner out of the way, closed with its x. Shown
// once per device, timed ones once per meeting.
const PUSH_CHARS = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';
const pushPrefix = (ms) => { let key = ''; for (let i = 0; i < 8; i++) { key = PUSH_CHARS.charAt(ms % 64) + key; ms = Math.floor(ms / 64); } return key; };
// The timed announcements due at a moment: a class listing the student, meeting today, the
// announcement's time reached within the last two minutes and inside the meeting.
export function dueAnnouncements(classes, uid, tz, now = Date.now()) {
  const clock = zonedClock(tz, now), due = [];
  for (const [key, data] of Object.entries(classes || {})) {
    if (!data?.students?.[uid] || !data.days?.includes(clock.day) || !data.start || !data.end) continue;
    if (clock.minutes < clockMinutes(data.start) || clock.minutes >= clockMinutes(data.end)) continue;
    for (const [id, item] of Object.entries(data.announcements || {})) {
      if (!item?.time || !item.message) continue;
      const at = clockMinutes(item.time);
      if (clock.minutes >= at && clock.minutes < at + 2) due.push({ id: `${key}/${id}/${clock.date}`, ...item });
    }
  }
  return due;
}
let announcing = false;
const announced = new Set();
function startAnnouncements(uid) {
  if (announcing) return;
  announcing = true;
  // The classes, live: a change the teacher makes before class reaches the device at once.
  onValue(ref(database, `/teachers/${z.teacher}/classes`), (snapshot) => { classCache = { time: Infinity, classes: snapshot.val() }; });
  onChildAdded(query(ref(database, `/teachers/${z.teacher}/broadcasts`), orderByKey(), startAt(pushPrefix(Date.now() - 60000))), (snapshot) => {
    const item = snapshot.val();
    if (item?.message && classCache.classes?.[item.class]?.students?.[uid]) showAnnouncement(`sent/${snapshot.key}`, item);
  });
  setInterval(() => {
    const tz = z.teachers?.[z.teacher]?.settings?.timezone;
    if (tz) for (const item of dueAnnouncements(classCache.classes, uid, tz, serverNow())) showAnnouncement(item.id, item);
  }, 15000);
}
function showAnnouncement(id, item) { try {
  if (announced.has(id)) return;
  announced.add(id);
  try { if (localStorage.getItem(`announced:${id}`)) return; localStorage.setItem(`announced:${id}`, '1'); } catch (error) { /* a private window: the in-memory set is enough */ }
  // Written on the session record, never drawn here: drawn from the echo on every session that follows
  // this one (drawAnnouncements, drawDialogs), so a teacher watching the tile sees what the student sees.
  if (item.blocking) askDialog(z.session.key, { required: true, header: 'Announcement', message: escapeHtml(item.message).replace(/\n/g, '<br>') });
  else update(ref(database, `/teachers/${z.teacher}/online/${z.session.key}/announcements`), { [id.replace(/\//g, '_')]: { message: item.message } });
} catch (error) { logError(error, 'showing an announcement'); }}

// The announcement banners showing on a session (`/online/{session}/announcements/{id}` = { message }),
// drawn on the student's own device and on a tile watching it alike, from the session listener's echo
// (Event.listenSession). Closing one anywhere removes it from the record, which takes it off every
// screen. A blocking announcement is a dialog (askDialog).
export function drawAnnouncements(doc, list, path) { try {
  for (const shown of doc.querySelectorAll('[data-announcement]')) if (!list?.[shown.dataset.announcement]) shown.remove();
  for (const [id, item] of Object.entries(list || {})) {
    if (!item?.message || doc.querySelector(`[data-announcement="${id}"]`)) continue;
    const close = () => remove(ref(database, `${path}/${id}`));
    const message = escapeHtml(item.message).replace(/\n/g, '<br>');
    let box = doc.getElementById('announcements');
    if (!box) {
      box = doc.createElement('div');
      box.id = 'announcements';
      doc.body.appendChild(box);
    }
    const banner = doc.createElement('div');
    banner.classList.add('announcement', 'ui');
    banner.dataset.announcement = id;
    banner.innerHTML = `<span class="announcement-text">${message}</span>`;
    const button = doc.createElement('button');
    button.classList.add('announcement-close');
    button.title = 'Close';
    button.innerHTML = z.svg?.icons?.close || '×';
    act(button, 'click', close);
    banner.appendChild(button);
    box.appendChild(banner);
  }
} catch (error) { logError(error, 'drawing the announcements'); }}

// First stamp for a session record the dashboard creates for someone else (a pinned code's
// teacher record, a report tile). Ungated: the sweep deletes a teacher-keyed record with no
// heartbeat, so the stamp has to land with the record.
export function stampSession(path) {
  return update(ref(database, `${path}/time`), { active: serverTimestamp(), connected: serverTimestamp() }).catch((error) => logError(error, `stamping ${path}`));
}

export function truncate(container, string, context) {
  try {
    const words = string.split(' ');
    container.title = string;
    container.innerText = string;
    // Fast path: full string fits — no truncation needed.
    if (container.scrollWidth <= container.offsetWidth) return;
    // Binary search for the largest k in [0, words.length - 1] such that
    // `${words.slice(0, k).join(' ')}...` fits within offsetWidth.
    let lo = 0, hi = words.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      container.innerText = `${words.slice(0, mid).join(' ')}...`;
      if (container.scrollWidth <= container.offsetWidth) lo = mid;
      else hi = mid - 1;
    }
    container.innerText = `${words.slice(0, lo).join(' ')}...`;
  } catch (error) { logError(error, context); }
}

// The on-screen window of the canvas square, in canvas units, for whatever document is
// handed in. ONE function so the student's device and the teacher's tile can never measure
// it differently - the whole point is to compare two numbers produced the same way. Returns
// null before the canvas exists or while it has no layout, and the caller treats that as
// "nothing to say" rather than as a full-screen band.
export function measureDrawable(doc, viewWidth, viewHeight) { try {
  // The OPEN slide's canvas only. Every problem on the page carries its own canvas, each with
  // its own scroll, and falling back to the first one in the document measured a slide nobody
  // was looking at — and published that as the student's window.
  const canvas = doc.querySelector('.slide-div.current .canvas-div canvas.canvas');
  if (!canvas?.width) return null;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  // The rect already carries .canvas-div's CSS transform (mobile scales it 2x), so this
  // ratio stays honest whichever breakpoint the document landed on - which matters, because
  // the two sides can land on DIFFERENT breakpoints and that is one of the things this is
  // meant to catch.
  const units = canvas.width / rect.width;
  const clamp = (v) => Math.max(0, Math.min(canvas.width, Math.round(v)));
  return {
    drawX1: clamp(Math.max(0, -rect.left) * units),
    drawY1: clamp(Math.max(0, -rect.top) * units),
    drawX2: clamp(Math.min(rect.width, viewWidth - rect.left) * units),
    drawY2: clamp(Math.min(rect.height, viewHeight - rect.top) * units),
    drawSize: canvas.width
  };
} catch (error) { logError(error, 'measuring the drawable area'); return null; } }

export function updateScreen(doc, win, session, event) { try {
  // documentElement.clientWidth/clientHeight is the CSS layout viewport — the
  // box CSS computations resolve against. Single source of truth for every
  // width/height read on the student side: same number on desktop, stable
  // across iOS Safari URL-bar animations (innerHeight goes stale mid-animation;
  // clientHeight does not), doesn't include scrollbar widths, doesn't lie
  // under pinch-zoom (zoom doesn't change layout size). Used for both the
  // rtdb write (so the dashboard tile sizes/aspect-ratio match what the
  // student actually has laid out) AND the local --app-* vars (so the
  // student's own UI uses the same number it just published).
  const root = doc.documentElement;
  const screenWidth = root.clientWidth;
  const screenHeight = root.clientHeight;
  if (screenWidth < 600) root.classList.add('mobile');
  else root.classList.remove('mobile');
  // The open slide's scroll. A bare '.canvas-div' is the FIRST problem on the page, so every
  // other problem published problem 1's scroll (usually none).
  const canvasDivEl = doc.querySelector('.slide-div.current .canvas-div');
  const scrollValue = canvasDivEl ? canvasDivEl.style.getPropertyValue('--scroll') : '';
  // The VISUAL viewport, published beside the layout viewport rather than instead of it.
  // The two differ exactly where the reported "some iPads" drawable mismatch lives: the
  // on-screen keyboard and any standing zoom state shrink only the visual viewport, so a
  // student can be seeing a CROP of the layout the record describes - and the teacher,
  // whose tile is sized from that record, can draw in the band the student cannot see.
  // width/height stay the layout viewport on purpose: they drive the tile's aspect, the
  // mobile class, and the student's own --app-* layout, and the layout viewport is the
  // space the canvas square is anchored in. The visual fields are informational, for the
  // dashboard to mark the band. offsetLeft/offsetTop locate the visible crop within the
  // layout viewport (nonzero under zoom); rounded, since iOS reports fractions.
  const viewport = win.visualViewport;
  const visual = viewport ? {
    visualHeight: Math.round(viewport.height),
    visualLeft: Math.round(viewport.offsetLeft),
    visualTop: Math.round(viewport.offsetTop),
    visualWidth: Math.round(viewport.width)
  } : { visualHeight: screenHeight, visualLeft: 0, visualTop: 0, visualWidth: screenWidth };
  // WHAT THE STUDENT CAN ACTUALLY SEE, measured rather than modelled. The two viewport
  // figures above can only ever disagree in two states - an on-screen keyboard, and a pinch
  // being held - and this app has neither: the keyboard is a custom one that never shrinks
  // the viewport, and gestures are disabled. They therefore agree on every device, always,
  // which is why everything derived from their difference was inert. This is the quantity
  // that actually matters for drawing: the window of the canvas square that is ON SCREEN,
  // in canvas units, taken by intersecting the canvas element's own rect with the viewport.
  // The dashboard measures the same thing inside its tile and shades the difference, so
  // whatever a given device is doing to make the two differ, the band is correct without
  // anyone having to model the cause first.
  const drawable = measureDrawable(doc, screenWidth, screenHeight);
  if (session?.type === 'student') {
    // Dedup: only write when something actually changed.
    const sig = `${screenHeight}|${screenWidth}|${scrollValue || '0px'}|${visual.visualWidth}x${visual.visualHeight}@${visual.visualLeft},${visual.visualTop}|${drawable ? `${drawable.drawX1},${drawable.drawY1},${drawable.drawX2},${drawable.drawY2}/${drawable.drawSize}` : 'none'}`;
    if (win._lastScreenSig !== sig) {
      win._lastScreenSig = sig;
      // With no slide open the window is written as null rather than left out: an update merges,
      // so leaving it out kept the LAST slide's window on the record, and the next slide was
      // shaded against it until something else happened to republish.
      const bounds = drawable || { drawX1: null, drawY1: null, drawX2: null, drawY2: null, drawSize: null };
      safeUpdate(`/teachers/${z.teacher}/online/${session.key}/screen`, { ...visual, ...bounds,height: screenHeight, width: screenWidth, scroll: scrollValue || '0px' });
    }
  }
  // For focused dashboard student sessions, resizeSession manages --app-*.
  // Everywhere else, derive from the same layout viewport we just published.
  if (session?.type !== 'dash-student-online' || !root.classList.contains('focused-session')) {
    root.style.setProperty('--app-height', `${screenHeight - 36}px`);
    root.style.setProperty('--app-width', `${screenWidth}px`);
  }
  // Re-clamp any positioned panels (calculator, multiplication chart, board/chart/sheet)
  // against the current viewport. Used to be gated on `event` being passed — that
  // worked for window resize handlers but skipped Game.resize() (which calls updateScreen
  // without an event), leaving the multiplication chart's inline `right: X%` drifting
  // against the new iframe dimensions whenever a teacher reshaped the dashboard grid.
  // forEach is a no-op when no panels are open, so the cost on cold paths is two
  // empty querySelectorAll calls.
  // Only when the space they are clamped against actually changed. updateScreen also runs on
  // a problem opening, closing and scrolling, and on close the panels are still inside the
  // slide that has just shrunk back to its grid cell: measured there, every panel was clamped
  // against a cell-sized box, written to rtdb, and pushed together on the echo.
  const scale = `${root.style.getPropertyValue('--session-scale-x')}x${root.style.getPropertyValue('--session-scale-y')}`;
  const space = `${win.innerWidth}x${win.innerHeight}|${scale}`;
  if (win._lastClampSpace === space) return;
  win._lastClampSpace = space;
  // (not an expanded notes page: it is placed by canvas.fitNotes, and saving where it sits would
  // overwrite the position it contracts back to)
  doc.querySelectorAll('.canvas-ui:not(.expanded)').forEach((element) => drag(element, doc, win, 0, 0, null, 'updating the screen'));
  doc.querySelectorAll('.panel').forEach((element) => drag(element, doc, win, 0, 0, null, 'updating the screen'));
} catch (error) { logError(error, 'updating screen'); }}

export async function write(obj, path, val, context) {
  try {
    let objData = obj;
    let objOutput = [];
    objOutput[0] = obj;
    let keyOutput = [];
    keyOutput[0] = '';
    let refOutput = [];
    refOutput[0] = '/';
    if (path.length === 0) return;
    if (!obj) obj = {};
    for (let i = 1; i <= path.length; i++) {
      if (path[i - 1] === '#') {
        let key = 0;
        while (obj.hasOwnProperty(key)) key++;
        path[i - 1] = key;
      } else if (String(path[i - 1])?.endsWith('*')) {
        let length = parseInt(path[i - 1]);
        let key = getRandomString(length);
        while (obj.hasOwnProperty(key)) key = getRandomString(length);
        path[i - 1] = key;
      }
      // Ensure intermediate value is an object so we can descend / assign children.
      // The old `if (!obj[key])` check only caught falsy values — a truthy primitive
      // (string/number/boolean) at any level let `obj` become a boxed primitive on
      // the next iteration, then `obj[key] = val` threw "Attempted to assign to
      // readonly property" on iOS Safari (the 28× notes-canvas error).
      if (Object(obj[path[i - 1]]) !== obj[path[i - 1]]) obj[path[i - 1]] = {};
      if (i === path.length) {
        obj[path[i - 1]] = val;
      } else {
        obj = obj[path[i - 1]];
        keyOutput[0] += `/${path[i - 1]}`;
        keyOutput[i] = path[i - 1];
        refOutput[i] = keyOutput[0];
      }
      let objNode = objData;
      for (let j = 1; j <= i; j++) {
        objOutput[i] = objNode[path[j - 1]];
        objNode = objNode[path[j - 1]];
      }
    }
    keyOutput[path.length] = path[path.length - 1];
    refOutput[path.length] = `${keyOutput[0]}/${keyOutput[path.length]}`;
    return { key: keyOutput, ref: refOutput, val: objOutput };
  } catch (error) {
    let message = `${z.name ? z.name : 'Someone'} encountered an error writing data to an object${context ? ` when ${context}` : ''}: ${error}`;
    console.error(message);
    logMessage(message, { app: z.app, code: z.code, session: z.session?.key, student: z.student, teacher: z.teacher, tooltip: navigator.userAgent, type: 'error', version: process.env.VERSION });
  }
}